/**
 * Le manuel de l'application, en données.
 *
 * Il vit ici, dans le code, et non en base — contrairement à tout le reste du
 * projet. La règle « rien en dur » vise les **données métier** : un prix, un
 * nom de plat, une phrase du site public. Ce texte-ci décrit le logiciel
 * lui-même, et doit donc changer en même temps que lui : le loger en base
 * garantirait qu'on ajoute un écran sans jamais penser à le documenter.
 *
 * Ce qui vient de la base, en revanche, c'est **la liste des permissions et la
 * composition des rôles** : les recopier ici les aurait fait dériver au premier
 * ajout. La page les lit en direct et signale ce qu'elle ne sait pas expliquer.
 *
 * Une seule langue, le français : un manuel d'exploitation est un travail de
 * traduction, pas de code, et trois versions à moitié écrites valent moins
 * qu'une seule qui dit vrai. La page l'annonce plutôt que de laisser croire à
 * un oubli.
 */

/** Une action, décrite comme on la ferait : qui, où, avec quoi, comment. */
export interface GuideProcedure {
  id: string
  title: string
  who: string
  where: string
  /** Ce qu'il faut avoir sous la main avant de commencer. */
  needs?: string
  steps: string[]
  /** Le piège qui coûte une soirée, quand il y en a un. */
  warning?: string
  /** Pourquoi c'est fait ainsi — utile quand la manœuvre surprend. */
  why?: string
}

export interface GuideChapter {
  id: string
  icon: string
  title: string
  /** Le moment du projet auquel ce chapitre appartient. */
  phase: 'comprendre' | 'preparer' | 'jour-j' | 'apres'
  intro: string
  /** Paragraphes libres, avant les procédures. */
  notes?: { title: string; body: string }[]
  procedures?: GuideProcedure[]
}

/** Les quatre moments, dans l'ordre où on les vit. */
export const PHASES: { id: GuideChapter['phase']; label: string; hint: string }[] = [
  {
    id: 'comprendre',
    label: 'Comprendre',
    hint: "Les notions sans lesquelles rien n'a de sens.",
  },
  {
    id: 'preparer',
    label: 'Préparer',
    hint: "Des mois avant. Tout ce qui se décide à froid.",
  },
  {
    id: 'jour-j',
    label: 'Le jour J',
    hint: "Debout, une main sur un téléphone.",
  },
  {
    id: 'apres',
    label: 'Après',
    hint: "Compter, expliquer, archiver, effacer.",
  },
]

/**
 * Ce que chaque rôle est censé faire, en une phrase de responsabilité.
 *
 * La composition exacte du rôle — sa liste de permissions — vient de la base.
 * Ici on ne dit que ce que la base ne peut pas dire : à quoi sert cette
 * personne, et ce qu'on attend d'elle.
 */
export interface RoleBrief {
  code: string
  /** Le nom qu'on emploie dans l'équipe, s'il diffère du libellé technique. */
  title: string
  mission: string
  /** Les écrans qu'elle ouvre vraiment. */
  screens: string[]
  /** Ce que le rôle ne permet pas, et pourquoi. */
  limits: string
  /** Combien de personnes tiennent ce poste, en pratique. */
  headcount: string
}

export const ROLE_BRIEFS: RoleBrief[] = [
  {
    code: 'ADMIN',
    title: 'Administrateur',
    mission:
      "Il monte l'édition et il arbitre. C'est le seul compte qui peut tout : créer les comptes et distribuer les rôles, configurer le serveur d'e-mails et les alertes, traiter les demandes RGPD, piloter le système de test depuis la production. Le soir J, il suit toute la soirée depuis la régie (/ops/live) et règle les alertes.",
    screens: ['Tout le back-office', 'Tous les écrans /ops'],
    limits:
      "Aucune limite technique — d'où la règle d'usage : deux administrateurs au maximum, en rôle global, et jamais un compte partagé. Le journal d'audit nomme des personnes, pas des fonctions.",
    headcount: '1 à 2',
  },
  {
    code: 'EVENT_MANAGER',
    title: 'Responsable événement',
    mission:
      "Il fait tourner l'édition : catalogue, billets, tables, commandes, programme, concours, votes (il lit le tableau privé des scores et peut annuler un bulletin), caisses, finances, stocks, service en salle, exports. Le soir J, c'est le responsable de soirée : il voit toute la régie (/ops/live) et peut ouvrir le conducteur du MC.",
    screens: ['Presque tout le back-office', '/ops/live', '/ops/bilan', '/ops/stock'],
    limits:
      "Il ne gère ni les comptes ni les rôles, ni le serveur d'e-mails et les alertes, ni le RGPD. Aujourd'hui il peut aussi purger des données, clôturer, archiver et dupliquer une édition : si ces gestes doivent rester à l'administrateur, il faut le demander.",
    headcount: '1 à 2',
  },
  {
    code: 'FINANCE_MANAGER',
    title: 'Responsable finances (trésorier)',
    mission:
      "Il tient le budget, saisit et valide les dépenses des autres, fixe les objectifs de recettes, lit les commandes et le chiffre d'affaires, voit la valeur des stocks, suit les sessions de caisse de toute l'équipe et ferme la caisse de quelqu'un d'autre. Il lit le bilan du service.",
    screens: ['Finances', 'Objectifs', 'Courses', 'Stocks', 'Clôture (lecture)', 'Service en salle (bilan seulement)', 'Journal', 'Régie /ops/live (caisses, espèces à remettre, ventes, stock, commandes par serveur)'],
    limits:
      "Il n'attribue pas les tables aux serveurs (droit service.table.assign, réservé au responsable de salle). Il n'approuve pas sa propre dépense. Il ne clôture pas l'édition, ne télécharge pas les exports CSV, ne joint pas de fichier à une dépense et ne valide pas de comptage de stock : ces gestes passent par l'administrateur ou le responsable événement. Il lit les commandes sans les modifier.",
    headcount: '1',
  },
  {
    code: 'PROJECT_MANAGER',
    title: 'Responsable projet',
    mission:
      "Il pilote la préparation : les tâches, leurs responsables, leurs échéances. Il lit l'édition, la liste des comptes et le tableau de bord, et les avis des invités (lecture seule) pour préparer l'édition suivante.",
    screens: ['Projet', 'Édition (lecture)', 'Avis des invités (lecture)', '/ops/live'],
    limits: "Aucun accès à l'argent ni au catalogue. Suivre l'avancement ne demande pas de voir les montants. La régie /ops/live s'ouvre, mais aucune de ses sections ne relève de son rôle : elle reste vide.",
    headcount: '1',
  },
  {
    code: 'AREA_MANAGER',
    title: 'Responsable de domaine (bar, cuisine…)',
    mission:
      "Il tient sa réserve : réceptionne les livraisons, déclare casses et retours, ajoute à la liste de courses ce qui manque, et suit ses tâches de projet.",
    screens: ['Stocks (/ops/stock)', 'Courses', 'Projet'],
    limits:
      "Il voit les quantités, pas les prix d'achat. Il enregistre les entrées et sorties de stock, mais pas les comptages physiques ni les corrections (inventory.movement.adjust, « Corriger un stock ») : le comptage se fait avec un responsable événement ou l'administrateur. Il ne marque pas un produit épuisé, sauf si on lui ajoute bar.order.read ou kitchen.order.read. L'administrateur peut lui accorder ces droits par un rôle sur mesure (Rôles).",
    headcount: '1 par domaine',
  },
  {
    code: 'CASHIER',
    title: 'Caisse',
    mission:
      "Il peut prendre n'importe quelle caisse (bar, cuisine, entrée, tout le catalogue) avec son fond, vend au comptoir, inscrit apports et sorties, reçoit les espèces des serveurs (Mon tiroir → Remises des serveurs), annule ses propres ventes du service en cours, et ferme en déclarant ce qu'il a compté.",
    screens: ['Caisse (/ops/pos)'],
    limits:
      "Sa caisse et rien d'autre : ni le budget, ni les réglages, ni les sessions de ses collègues. Une personne tient une caisse à la fois. Il a le droit de voir les montants des commandes et réservations : seul, il n'ouvre aucun écran — il sert le jour où on lui ouvre aussi les commandes.",
    headcount: 'Selon les caisses « tout le catalogue » et les renforts',
  },
  {
    code: 'SERVICE',
    title: 'Service en salle (serveur)',
    mission:
      "Il prend les commandes à ses tables, encaisse celles que les invités passent par le QR de table (espèces avec monnaie calculée, ou PayPal vérifié), suit la cuisine et le bar, et remet ses espèces au caissier d'un poste.",
    screens: ['Salle (/ops/service) — onglets du service'],
    limits:
      "Il ne place pas les groupes à leur table (c'est l'hôtesse, onglet « Placement ») — sauf si on lui donne aussi le rôle Hôtesse d'accueil : il bascule alors entre « Service » et « Placement » sur le même écran. Il ne tient pas de caisse : ses espèces restent « En poche », à son nom, jusqu'à la remise à la caisse d'un poste. Il n'encaisse que les commandes de ses tables, des tables sans serveur, ou celles qu'il a prises (sauf s'il tient une caisse, ou s'il est responsable de salle) ; il n'annule une commande réglée que s'il l'a encaissée lui-même.",
    headcount: '2 à 6 — selon le nombre de tables',
  },
  {
    code: 'STAFF',
    title: 'Équipe (porte)',
    mission:
      "Il tient l'entrée : scanne les QR et fait entrer nom par nom, encaisse sur le scan une commande « payer à l'entrée », vend les entrées à la porte, place les tables à leur numéro, remet les bracelets. Il ouvre, compte et ferme la caisse de l'entrée, y note apports et sorties, et y reçoit les remises des serveurs.",
    screens: ['Contrôle (/ops/checkin)', 'Caisse de l\'entrée (/ops/pos)', 'Salle → Placement (/ops/service, lecture)'],
    limits:
      "Il voit le placement des tables (qui est arrivé, qui est installé) sans pouvoir installer un groupe ni débloquer une table ; il ne change pas la table d'un groupe déjà installé (« Ce groupe est déjà installé… »). Pas de vente au comptoir : ses entrées passent par le compteur de la porte. Il ne tient que la caisse de l'entrée (ou une caisse « tout le catalogue »). Il voit les candidats, les tours et les votes, jamais les scores. Il n'ouvre ni la cuisine, ni le bar, ni les finances. Il peut lire les commandes et les réservations et en encaisser une hors ligne : c'est ce qui permet de faire payer à la porte. Il ne voit pas leurs montants (ni total, ni prix, ni colonne « Total ») : au scan, seul s'affiche ce qui reste à encaisser.",
    headcount: '2 à 4',
  },
  {
    code: 'HOSTESS',
    title: "Hôtesse d'accueil",
    mission:
      "Elle reçoit les groupes qui ont réservé une table, une fois passés à la porte. Sur l'écran Salle (/ops/service), onglet « Placement », elle voit en tête les groupes arrivés (le numéro de table en grand), les accompagne, et les marque « Installés » — la table est alors bloquée. Elle peut changer la table d'un groupe (avec un motif s'il est déjà assis) et annuler une installation faite par erreur (motif obligatoire). Chaque geste est enregistré à son nom.",
    screens: ['Salle (/ops/service) → Placement', 'Placement (/admin/placement), si elle a accès au back-office'],
    limits:
      "Ni argent ni commandes : aucun encaissement, aucune caisse, aucune commande. Elle ne scanne pas les billets et ne fait entrer personne : l'arrivée vient de la porte. Si elle sert aussi, on lui ajoute le rôle Service en salle : les onglets du service apparaissent sur le même écran.",
    headcount: '1 à 2',
  },
  {
    code: 'EMCEE',
    title: 'Maître de cérémonie',
    mission:
      "Il mène toute la soirée depuis un seul écran, son conducteur (/ops/deroule), en six onglets : « Direct » (étape en cours, suivante, retard sur le déroulé, « Décaler la suite »), « Programme » (son déroulé, ses sous-parties — pronostic, prestations des candidats, désignations, quiz du public, notes — et le Cake Moment), « Votes » (tous les votes, lien et QR du vote, ouvrir, geler, fermer, publier ou retirer un vote libre, votants et dépouillement), « Concours » (tours, tableau privé public 70 % + jury 30 %, départage, proclamation, publication des scores et envoi aux candidats, retours en arrière avec motif, podiums des prix), « Jury » (qui a noté quel tour, ajouter, renommer, remplacer ou révoquer un juré, montrer son lien personnel) et « Écran » (ce que la salle voit ; il y affiche et retire les candidats d'un pronostic ou un gagnant désigné dans la salle).",
    screens: ['Conducteur (/ops/deroule) — toute la soirée', 'Votes et Miss & Master (mêmes boutons, facultatif)', 'Programme (lecture)'],
    limits:
      "Hors de son déroulé, rien créer ni supprimer : ni concours, ni tour, ni vote, ni prix ; les nommés et les animations restent en lecture, aucun bulletin à annuler, aucune candidature à examiner. L'étape en cours ne se retire pas. L'adresse de l'écran de la salle (avec sa clé) reste à l'administration. Service, caisse, commandes, finances et RGPD ne sont pas les siens. Il n'ouvre pas la régie /ops/live. Ce qui se prépare avant (concours, tours, votes, nommés, prix, jurés) se crée dans le back-office.",
    headcount: '1',
  },
  {
    code: 'BAR_STAFF',
    title: 'Bar',
    mission:
      "Il voit arriver les commandes réglées sur l'écran du bar — numéro, table, serveur, chrono — les marque « Terminé », annule avec un motif ce qu'il ne peut pas servir, marque une boisson épuisée (« Marquer épuisé » / « Remettre en vente »). Il ouvre, compte et ferme la caisse du bar, y note apports et sorties, encaisse au bar une table venue payer, vend au comptoir ce que le bar sert, et reçoit les remises des serveurs.",
    screens: ['Bar (/ops/bar)', 'Caisse du bar (/ops/pos)'],
    limits: "Un seul poste, le bar, et seulement la caisse du bar (ou une caisse « tout le catalogue »). Aucune saisie de prix, aucun écran financier.",
    headcount: '1 à 3',
  },
  {
    code: 'KITCHEN_STAFF',
    title: 'Cuisine',
    mission:
      "Même chose côté cuisine : les commandes réglées avec leurs seuls plats, jusqu'à « Terminé » ; annuler une commande ; marquer un plat épuisé ; ouvrir, compter et fermer la caisse de la cuisine, apports et sorties, encaisser une table en cuisine, vendre au comptoir les plats et packs, recevoir les remises des serveurs.",
    screens: ['Cuisine (/ops/cuisine)', 'Caisse de la cuisine (/ops/pos)'],
    limits: "Aucun accès à l'écran du bar ni aux finances. Seulement la caisse de la cuisine (ou une caisse « tout le catalogue »).",
    headcount: '2 à 4',
  },
  {
    code: 'CUSTOMER',
    title: 'Client',
    mission:
      "Ce n'est pas un poste d'équipe : c'est le rôle des comptes clients, qui n'ouvre aucune surface d'exploitation.",
    screens: ['Mon billet'],
    limits: "Aucune permission. Il n'a rien à faire dans la liste des comptes d'équipe.",
    headcount: '—',
  },
]

/** Ce que chaque module de permissions gouverne, en une phrase. */
export const MODULE_BRIEFS: Record<string, string> = {
  admin:
    "Les comptes, les rôles, le journal d'audit, les alertes et le serveur d'e-mails, le RGPD, et le pilotage du système de test. Le module qui distribue les droits.",
  analytics: "Le tableau de bord, le chiffre d'affaires détaillé et les exports CSV.",
  bar: "L'écran du bar : la file des boissons à préparer.",
  catalog: 'Les plats, les packs, les boissons, leurs catégories, et le marquage « épuisé ».',
  checkin: "Le contrôle à l'entrée : scan des QR, annulation d'un pointage, historique.",
  competition: 'Miss & Master : catégories, candidatures, tours, jury, proclamation.',
  event: "L'édition elle-même : configuration, réglages, contenus, médias, clôture, purge.",
  feedback:
    "Les avis des invités, anonymes (/avis) : lire les réponses et leur analyse, les exporter, supprimer un spam (motif obligatoire), ouvrir ou fermer le recueil, réécrire les textes du formulaire, envoyer le lien aux invités.",
  finance: 'Budget, dépenses, recettes ventilées, objectifs, liste de courses, rapport.',
  inventory: 'Le journal des stocks, les comptages, et la valeur de la réserve.',
  kitchen: "L'écran de la cuisine : la file des plats à préparer.",
  orders:
    "Les commandes : consultation, encaissement hors ligne, annulation, remboursement, suppression. « Voir les montants des commandes et réservations » est à part : sans lui, les montants sont retirés des réservations, des commandes et de l'export (la porte).",
  pos: 'Les caisses : caisses physiques, sessions, ventes au comptoir, fermeture.',
  pricing: 'Les règles de prix : remises, suppléments, codes promo.',
  program: "Le déroulé de la soirée et son avancement en direct.",
  project: 'La préparation : catégories, tâches, statuts, réunions.',
  service:
    "Le service en salle : les serveurs, leurs tables, les commandes prises à table, les annulations, et le bilan par serveur, par poste et par caisse.",
  single: "Le bracelet « célibataire » : qui en attend un, et sa remise à l'entrée.",
  tables: 'Les types de tables, les formules, le plan de salle, les réservations et leur placement.',
  ticketing: "Les types de billets, les billets émis, les participants.",
  voting: 'Les votes : leur conduite, les scores avant publication, l\'annulation d\'un bulletin.',
}

export const CHAPTERS: GuideChapter[] = [
  // ── Comprendre ───────────────────────────────────────────────────
  {
    id: 'organisation',
    icon: '🗺️',
    phase: 'comprendre',
    title: 'Comment le système est organisé',
    intro:
      "Trois portes d'entrée : le site public, le back-office (/admin) et les écrans d'équipe (/ops/…). Et deux sites : la production, et une copie pour répéter.",
    notes: [
      {
        title: 'Le site public',
        body:
          "/, /billets, /carte, /programme, /concours, /candidats, /vote, /resultats, /infos, /equipe, et les pages légales. Les clients y achètent ; « Mon billet » (/mon-billet) leur rend leurs QR ; /commander?table=… est la commande depuis la table.",
      },
      {
        title: 'Les écrans d\'équipe, un par poste',
        body:
          "Porte : /ops/checkin · Salle (serveurs et hôtesses, onglets selon le rôle) : /ops/service · Cuisine : /ops/cuisine · Bar : /ops/bar · Caisse d'un poste : /ops/pos · Conducteur du maître de cérémonie : /ops/deroule · Régie : /ops/live · Bilan et exports : /ops/bilan · Réserve : /ops/stock. Chaque écran a sa propre connexion (« Connexion équipe ») et revient au même écran. Un mot de passe provisoire doit être remplacé (12 caractères au moins, « Choisissez votre mot de passe ») avant que l'écran s'ouvre. Un compte sans le droit voit « Votre rôle ne permet pas cet écran », avec un bouton « Changer de compte ». L'ancienne adresse /ops/caisse n'encaisse plus rien : elle renvoie chacun vers son écran selon son rôle (serveur → /ops/service, bar → /ops/bar, cuisine → /ops/cuisine, porte → /ops/checkin, caisse → /ops/pos). Tous ces liens, avec leur QR, sont dans QR & liens → « Écrans de l'équipe ».",
      },
      {
        title: 'Trois pages sans compte',
        body:
          "L'écran de la salle (/ecran?key=…&slug=… — l'adresse complète se copie dans Programme → « Écran de la salle »), la notation du jury (/jury, lien personnel de chaque juré), et la commande depuis la table (/commander?table=…).",
      },
      {
        title: 'La langue, sur chaque écran',
        body:
          "Chaque page a son sélecteur DE · FR · EN : le site public (en haut et en pied de page), le back-office (en bas du menu, et sur la connexion), chaque écran /ops (à côté de « Se déconnecter », et dès le formulaire de connexion), les pages ouvertes par un lien ou un QR (/vote, /jury, /mon-billet, /commander, /commande, /mes-resultats), et l'écran de la salle (petit coin en bas à droite, qui s'efface après quelques secondes sans souris). Le choix est gardé sur l'appareil. Pour imposer une langue par un lien ou un QR, ajouter ?lang=de, ?lang=fr ou ?lang=en à l'adresse (ou &lang=… s'il y a déjà un « ? ») : par exemple /commander?table=A3&lang=en, ou l'adresse de l'écran de la salle suivie de &lang=de. La langue est alors retenue et le paramètre disparaît de l'adresse.",
      },
      {
        title: 'Production et environnement de test',
        body:
          "https://leyamo.test.tas-platform.com est une copie de la production pour répéter. On s'y connecte avec ses identifiants habituels. Aucun e-mail ne part vers un client, aucun argent ne bouge (les liens PayPal y mènent nulle part), un bandeau rayé « ENVIRONNEMENT DE TEST » est en haut de chaque page. Les QR des vrais clients n'y ouvrent rien ; une commande passée sur le test a ses QR normaux. Ne jamais donner cette adresse à un client.",
      },
      {
        title: 'Ce qui a (enfin) un écran, et ce qui n\'en a pas',
        body:
          "Ont désormais un écran : les récompenses et leur podium (Miss & Master → Récompenses), le LeYAMO Cake Moment (Programme → Cake Moment), les dates d'ouverture et de clôture des candidatures (fiche de la catégorie) et la fenêtre d'un vote (fiche du vote), l'adresse de l'écran de la salle (Programme → Écran de la salle). Restent sans écran, et demandent le technicien (/api/docs) : les réunions, commentaires et pièces jointes du projet, et la valeur de la clé d'écran (SCREEN_ACCESS_KEY, dans la configuration du serveur).",
      },
    ],
    procedures: [
      {
        id: 'systeme-test',
        title: 'Rafraîchir ou remettre à zéro le système de test',
        who: 'Administrateur uniquement (admin.test_env.manage)',
        where: 'Back-office de la PRODUCTION → Système de test (/admin/systeme-test)',
        steps: [
          "Lire le bloc « État » : « Lanceur sur le serveur » (Actif / Ne répond plus / Non installé), « Site de test » (Répond), versions de la production et du test (Identiques / Différentes), « Dernière copie des données ».",
          "« Actualiser » → « Oui, actualiser » : le test passe sur la même version que la production, puis repart d'une copie fraîche de ses données. Après chaque mise à jour du site.",
          "« Réinitialiser » → « Oui, réinitialiser » : le test garde sa version et repart d'une copie fraîche des données de la production. Avant chaque répétition.",
          "La demande passe « En attente » : le lanceur du serveur la prend dans la minute (« En cours »), puis « Réussie » ou « Échouée ». L'écran suit tout seul et montre la fin du journal. « Retirer la demande » l'annule tant qu'elle est en attente.",
          "Une seule demande à la fois ; une copie dure plusieurs minutes. L'« Historique » garde qui a demandé quoi.",
        ],
        warning:
          "Les testeurs perdent tout ce qu'ils ont fait sur le test depuis la dernière copie (commandes, votes, comptes, réglages) : prévenez-les avant. La production n'est jamais modifiée, seulement lue. Sur le site de test lui-même, la page dit « Vous êtes sur le site de test » et ne montre aucun bouton.",
        why: "Le test (https://leyamo.test.tas-platform.com) n'envoie jamais de vrai e-mail et ne prend jamais de vrai paiement. « Le lanceur n'est pas installé sur le serveur » : l'installation est décrite dans docs/07-RUNBOOK.md, section E.8.",
      },
    ],
  },
  {
    id: 'notions',
    icon: '🧭',
    phase: 'comprendre',
    title: 'Les cinq notions de base',
    intro:
      "Cinq idées expliquent la quasi-totalité des surprises qu'on peut avoir devant un écran.",
    notes: [
      {
        title: '1. Une édition',
        body:
          "Tout est rattaché à une édition — « THE MASKED NIGHT 2026 ». L'édition affichée est en haut à gauche ; s'il y en a plusieurs, un sélecteur permet de changer et il est retenu sur l'appareil. Si vous ne voyez pas vos données, c'est presque toujours qu'une autre édition est sélectionnée.",
      },
      {
        title: '2. Billet ≠ réservation',
        body:
          "Un billet fait entrer une personne et porte un QR. Une réservation retient une table, des plats, des bouteilles. Une table dont la case « Donne l'entrée » est cochée fait entrer ses convives : le système émet alors un billet par convive, du type nommé dans le réglage « Type de billet pour l'entrée comprise dans une table ».",
      },
      {
        title: "3. Rien n'est écrit en dur",
        body:
          "Aucun prix, aucun texte, aucun nom de catégorie ne vit dans le programme. Tout se saisit dans le back-office.",
      },
      {
        title: '4. Le serveur décide des montants',
        body:
          "Un total affiché est toujours recalculé par le serveur informatique (« Total calculé par le serveur »). On ne corrige pas un montant à la main : on corrige le tarif dans le catalogue.",
      },
      {
        title: '5. Tout laisse une trace',
        body:
          "Annuler, rembourser, corriger un stock, approuver une dépense, fermer la caisse d'un autre : tout est inscrit au Journal, avec l'auteur, la date, l'avant et l'après. Le journal ne s'efface pas.",
      },
    ],
  },
  {
    id: 'roles-portee',
    icon: '🔑',
    phase: 'comprendre',
    title: 'Comptes, rôles et droits',
    intro:
      "Une personne = un compte. Un compte porte un ou plusieurs rôles. Un rôle est un paquet de droits. La liste réelle des droits de chaque rôle est lue en direct plus bas.",
    notes: [
      {
        title: 'Limité à une édition, ou global',
        body:
          "« Limiter à cette édition » (coché par défaut) : le rôle ne vaut que pour l'édition en cours, et la personne n'a plus rien l'année suivante sans qu'on ferme son compte. À utiliser pour toute l'équipe d'un soir. Décoché, le rôle suit la personne partout : à réserver à l'administration.",
      },
      {
        title: 'Le piège qui coûte une soirée',
        body:
          "Quelqu'un a reçu un rôle et « ne voit rien » : presque toujours, le rôle est limité à une autre édition que celle sélectionnée.",
      },
      {
        title: 'Deux rôles plutôt qu\'un rôle sur mesure',
        body:
          "Les droits de deux rôles s'additionnent : c'est presque toujours la bonne réponse à un besoin particulier. Sinon, l'administrateur compose un rôle sur mesure dans Rôles (procédure ci-dessous). Un rôle sans fiche dans ce manuel est signalé en orange plus bas.",
      },
      {
        title: 'Qui reçoit quoi le soir J',
        body:
          "Direction : Administrateur. Responsable de soirée : Responsable événement. Trésorier : Responsable finances. Porte : Équipe. Serveurs : Service en salle. Barmen : Bar. Cuisine : Cuisine. Caissier volant : Caisse. Maître de cérémonie : Maître de cérémonie. Responsable du vote (facultatif, le MC peut tout faire seul) : Maître de cérémonie ou Responsable événement. Jurés : aucun compte, un lien. Réserve : Responsable de domaine.",
      },
      {
        title: 'Les caisses des postes',
        body:
          "Une caisse par poste : l'argent d'un poste entre dans la caisse de ce poste (ou une caisse « tout le catalogue »), sinon c'est refusé. Bar, Cuisine et Équipe (porte) ouvrent chacun la caisse de leur propre poste, sans rôle Caisse en plus ; le rôle Caisse peut prendre n'importe laquelle. Les serveurs n'ont pas de caisse : leurs espèces restent « En poche » jusqu'à la remise. Le Responsable finances lit le bilan du service et ferme la caisse de quelqu'un d'autre ; attribuer les tables est réservé au responsable de salle (service.table.assign).",
      },
      {
        title: 'Un bouton manque ?',
        body:
          "Les écrans cachent ce que le compte n'a pas le droit de faire : ouvrir un vote, lire le tableau privé, gérer le concours, révéler une récompense, piloter le conducteur. Un bouton absent est presque toujours un droit absent — voir la liste des droits plus bas.",
      },
    ],
    procedures: [
      {
        id: 'equipe-creation',
        title: 'Créer un compte et lui donner un rôle',
        who: 'Administrateur',
        where: 'Équipe → Comptes',
        needs: "La liste des personnes et de ce qu'elles vont tenir",
        steps: [
          "« Créer un compte » : « Adresse e-mail » (c'est l'identifiant), « Nom complet », téléphone.",
          "Le mot de passe est tiré au sort et affiché une seule fois sous « Mot de passe provisoire » : « Copier », le transmettre, puis « C'est noté ».",
          "Ouvrir le compte → « Rôles » → « Ajouter un rôle » → « Choisir un rôle… » → laisser « Limiter à cette édition » coché → « Attribuer ».",
          "Vérifier la ligne : « sur « THE MASKED NIGHT 2026 » » (limité) ou « toutes éditions » (global).",
          "Faire tester la connexion par la personne, sur son écran, avant le soir J. À sa première connexion, elle doit remplacer le mot de passe provisoire (12 caractères au moins) : au back-office, « Mon compte » s'ouvre tout seul ; sur un écran /ops, « Choisissez votre mot de passe » → « Enregistrer et continuer ». Jusque-là, son compte porte le badge « mot de passe provisoire ».",
        ],
        warning:
          "Jamais de compte partagé : le journal nomme des personnes. Après 5 mots de passe faux, un compte est bloqué 15 minutes.",
      },
      {
        id: 'mon-compte',
        title: 'Changer son mot de passe et sa langue',
        who: 'Chaque membre de l\'équipe, pour son propre compte',
        where: 'Mon compte (/admin/compte), en bas du menu, sous son adresse',
        steps: [
          "« Mot de passe actuel », « Nouveau mot de passe » (12 caractères au moins), « Confirmer le nouveau mot de passe » → « Changer le mot de passe ». Les autres appareils sont déconnectés ; celui-ci reste connecté.",
          "« Langue » : enregistrée sur le compte, elle suit la personne sur tous ses appareils (le sélecteur « Langue » en bas du back-office fait la même chose).",
          "Le nom et l'adresse ne se corrigent pas ici : les demander à l'administrateur.",
        ],
        warning: "Tant qu'un mot de passe provisoire n'est pas remplacé, « Mon compte » s'ouvre tout seul (« Mot de passe à remplacer ») et aucune autre page n'est accessible.",
      },
      {
        id: 'roles-sur-mesure',
        title: 'Composer un rôle sur mesure',
        who: 'Administrateur',
        where: 'Rôles (/admin/roles)',
        steps: [
          "« Créer un rôle » : « Code » (majuscules, chiffres et « _ », ex. RESPONSABLE_BAR — il ne change plus ensuite), « Nom », « Description ».",
          "Cocher les « Permissions » module par module (« Tout le module » coche un bloc entier ; « sensible » signale argent, données personnelles, réglages critiques). Enregistrer.",
          "Le donner ensuite à la personne dans Équipe, comme un rôle du système.",
        ],
        warning:
          "Les permissions grisées sont celles que vous ne détenez pas : on ne donne pas un droit qu'on n'a pas. Pour partir d'un rôle existant : l'ouvrir → « Dupliquer ».",
        why: "Exemple : un responsable de domaine qui doit aussi faire les comptages et marquer un produit épuisé reçoit un rôle avec inventory.movement.adjust et bar.order.read (ou kitchen.order.read).",
      },
      {
        id: 'roles-modifier',
        title: 'Modifier ou supprimer un rôle',
        who: 'Administrateur',
        where: 'Rôles (/admin/roles) → ouvrir le rôle',
        steps: [
          "Chaque ligne indique son badge (« système », « sur mesure » ou « verrouillé »), le nombre de permissions et d'attributions.",
          "Modifier : « Nom », « Description », puis cocher ou décocher les « Permissions » (« Rechercher une permission » filtre la liste ; « Tout le module » coche un bloc). « Vérifier et enregistrer » montre les permissions ajoutées et retirées, et combien d'attributions sont touchées → « Confirmer ». L'effet est immédiat.",
          "« Qui porte ce rôle » liste les comptes, avec « Toutes les éditions » ou l'édition concernée.",
          "Supprimer (rôle sur mesure seulement) : « Supprimer ce rôle ». S'il est encore porté, choisir le « Rôle de remplacement » : chaque attribution passe à ce rôle, sur la même portée. « Supprimer le rôle » → « Confirmer la suppression ». Un rôle sur mesure que personne ne porte se supprime aussi par la croix de la liste.",
        ],
        warning:
          "« Administrateur » est verrouillé : ni modifiable ni supprimable (« Dupliquer » reste possible). Les rôles système se modifient mais ne se suppriment pas ; un retrait de permission y est conservé lors des mises à jour, et une permission nouvelle du logiciel leur arrive d'elle-même. Vous ne pouvez pas retirer de vos propres rôles le droit de gérer les rôles : demandez-le à un autre administrateur.",
      },
      {
        id: 'equipe-depart',
        title: 'Retirer l\'accès à quelqu\'un',
        who: 'Administrateur',
        where: 'Équipe → ouvrir le compte',
        steps: [
          "Décocher « Compte actif » : la personne ne peut plus se connecter, ses traces restent. C'est le geste normal.",
          "« Réinitialiser le mot de passe » : un nouveau mot de passe provisoire, et les sessions ouvertes sont coupées.",
          "« Supprimer le compte » → « Oui, supprimer » : à éviter, le nom ne reste plus qu'au journal.",
        ],
      },
    ],
  },

  // ── Préparer ─────────────────────────────────────────────────────
  {
    id: 'monter-edition',
    icon: '🎭',
    phase: 'preparer',
    title: "L'édition et ses réglages",
    intro:
      "On renseigne l'édition, puis les réglages qui décident de la vente. Le bloc « Préparation » de l'écran Édition coche les 15 étapes ; seules les deux premières bloquent la publication.",
    procedures: [
      {
        id: 'edition-config',
        title: "Renseigner l'édition",
        who: 'Administrateur, Responsable événement',
        where: 'Édition (/admin)',
        needs: 'Dates, lieu, logo, photo',
        steps: [
          "« Identité » : « Nom », « Sur-titre », « Slogan », « Accroche » (mots séparés par des points), « Description ».",
          "« Quand » : « Ouverture des portes », « Début », « Fin », capacité. Les heures sont dans le fuseau de l'édition.",
          "« Visuels » : « Logo » (bandeau et e-mails), « Photo d'ambiance ». « Où » : « Lieu », « Ville », « Adresse », « Code postal ». « Contact » : e-mail et téléphone.",
          "« Enregistrer » dans la barre « Modifications non enregistrées ».",
          "Lire « À traiter » (les alertes du moment) et « Préparation » (ce qui reste à faire).",
        ],
      },
      {
        id: 'edition-cycle',
        title: 'Créer, publier, archiver, dupliquer une édition',
        who: 'Administrateur (créer) ; Administrateur, Responsable événement (dupliquer, publier, archiver)',
        where: 'Édition → blocs « Éditions » et « Statut de l\'édition »',
        steps: [
          "« Éditions » liste toutes les éditions ; « Ouvrir » passe sur une autre, celle qu'on règle est « En cours ».",
          "« Nouvelle édition vierge » (Administrateur seulement) : « Identifiant (slug) », « Année », nom, début, fuseau, devise → « Créer l'édition ». Elle naît en brouillon.",
          "« Dupliquer pour l'an prochain » : nouvel identifiant, année, début, puis les cases (contenus du site, carte et boissons, billets, règles de prix et codes promo, tables, programme sans horaires, concours ; « Reprendre les stocks de l'an passé » décoché par défaut) → « Dupliquer l'édition ». Un rapport dit ce qui a été recopié.",
          "« Statut de l'édition » : « Publier » (refusé tant qu'une étape « bloquant » de la Préparation n'est pas faite), « Repasser en brouillon », « Archiver » (irréversible), « Clôturer… » (renvoie à Clôture).",
          "Donner aux membres de l'équipe leurs rôles sur la nouvelle édition.",
        ],
        warning:
          "Une nouvelle édition, vierge ou dupliquée, a « Ventes ouvertes » à non : ouvrir la vente est un geste à part, dans Réglages. La duplication recopie les réglages — y compris les liens PayPal, à vérifier —, recopie les codes promo désactivés, et ne recopie jamais commandes, billets vendus, candidats, votes, résultats, finances ni journal.",
      },
      {
        id: 'reglages',
        title: 'Régler la vente et les paiements',
        who: 'Administrateur, Responsable événement',
        where: 'Réglages',
        steps: [
          "Chaque réglage s'enregistre seul dès qu'on le change ; « par défaut » signale un réglage jamais touché. Les montants sont en euros. Onglets : « Pages du site », « Vente », « Tables », « Soirée sur place », « Compte à rebours », « Concours », « Bracelet célibataire », « Règles et mentions », « Contact », « Écran de salle », « Projet ».",
          "« Ventes ouvertes » (non par défaut, aussi pour une nouvelle édition) : sans lui, personne ne peut acheter.",
          "Moyens : « Régler en ligne par lien PayPal », « Régler en espèces à l'entrée », « Régler par PayPal à l'entrée ».",
          "« Liens de paiement PayPal (un par ligne) » : un lien par compte (« + Ajouter une ligne »). « Règlements par lien avant de changer » (5) : les commandes sont réparties à tour de rôle entre les comptes. Lien et référence sont figés sur chaque commande.",
          "« Préfixe de référence — entrées / tables / nourriture » (ENT, TBL, ESS) : la référence que le client écrit dans son paiement.",
          "« Consignes de règlement hors ligne » et « Consignes pour le règlement à l'entrée » : les textes montrés au client ({{link}}, {{reference}}).",
          "« Envoyer le lien de paiement automatiquement » : non par défaut — l'équipe relit chaque commande, puis envoie le lien d'un clic.",
          "Paiement en deux fois des tables : « Proposer le paiement en deux fois pour les tables », possible à partir de 13 jours avant, 60 % dus 8 jours avant, le reste 2 jours avant ; sinon paiement unique sous 48 h, au plus tard 3 jours avant.",
        ],
        warning:
          "« Type de billet pour l'entrée comprise dans une table » (onglet Tables) doit contenir le code d'un billet à 0 € (« Entrée table », non en vente). Vide, une table qui « donne l'entrée » n'admet personne : la Préparation le signale à l'étape 5 (« entrée comprise : billet support non renseigné ✗ ») et bloque la publication.",
      },
      {
        id: 'reglages-soiree',
        title: 'Les réglages du soir J',
        who: 'Administrateur, Responsable événement',
        where: 'Réglages',
        steps: [
          "« Commande à table active » : non par défaut. À ouvrir le soir J, à fermer en fin de service.",
          "« Montant minimum pour le service à table » (30 €) : en dessous, l'invité lit que sa commande est à retirer au comptoir.",
          "« Paiement en espèces autorisé » : les invités peuvent choisir espèces sur /commander (un serveur peut toujours encaisser en espèces).",
          "« Commande sans table autorisée » (oui par défaut) : propose « Je n'ai pas de table » sur /commander — l'invité commande avec son prénom et retire au comptoir. Fermé, le bouton disparaît et la commande sans table est refusée ; les serveurs et le comptoir peuvent toujours en prendre une.",
          "« Seuil d'alerte de stock » (10) ; onglet « Écran de salle » : « Rafraîchissement de l'écran » (15 s, aussi le rythme de la régie) et « Alerte d'attente (minutes) » (15 : au-delà, une commande réglée que la cuisine ou le bar n'a pas terminée est signalée dans la régie /ops/live) ; onglet « Bracelet célibataire » : « Bracelet célibataire actif ».",
          "« Pages du site » : un interrupteur par rubrique (Carte, Programme, Concours, Candidats, Résultats, Infos, Équipe).",
          "« Pages du site » aussi : « Bulle des visiteurs sur l'accueil » (oui) affiche en haut de l'accueil « ● 12 en ce moment · 3 480 visiteurs ». Le total est exactement « Visiteurs depuis le début » de l'écran Audience (un visiteur compté une fois par jour, journées additionnées) ; « en ce moment » compte les visiteurs dont la page est restée ouverte et visible dans les 5 dernières minutes — le même chiffre que la tuile « En ce moment » d'Audience. « Bulle des visiteurs : total affiché à partir de » (0) : en dessous, la bulle ne montre que les visiteurs du moment. Sans cookie ni IP conservée ; les robots et les écrans de l'équipe ne comptent pas. Un changement se voit sur le site en moins d’une minute, sans recharger la page.",
          "« Concours » : « Billet requis pour candidater » (non) — coché, une candidature n'est acceptée qu'avec un billet payé à la même adresse. « Contact » : « Numéro WhatsApp » — rempli, un bouton WhatsApp apparaît dans le pied de page et sur Infos. « Règles et mentions » : « Mention : l'entrée n'est pas incluse » (la règle du masque, elle, est le bloc de contenu mask_policy dans Contenus).",
        ],
      },
    ],
  },
  {
    id: 'alertes-equipe',
    icon: '📬',
    phase: 'preparer',
    title: 'Les e-mails : serveur, alertes, messages',
    intro:
      "Les e-mails aux clients partent par le serveur d'envoi configuré ici ; les alertes à l'équipe partent aux adresses abonnées, catégorie par catégorie.",
    procedures: [
      {
        id: 'serveur-envoi',
        title: "Configurer le serveur d'envoi",
        who: 'Administrateur',
        where: 'Notifications e-mail',
        needs: "L'adresse d'envoi et son mot de passe (Gmail : un « mot de passe d'application »)",
        steps: [
          "« Serveur SMTP », « Port », « Chiffrement » (Gmail : smtp.gmail.com, 587, STARTTLS).",
          "« Identifiant », « Mot de passe », « Adresse d'expédition », « Nom de l'expéditeur », « Adresse de réponse », puis « Enregistrer ».",
          "« Tester la connexion » → « Connexion et authentification réussies. »",
          "Cocher « Utiliser ce serveur », enregistrer, puis « Envoyer un e-mail d'essai à » votre propre adresse.",
        ],
        warning: "Le mot de passe n'est jamais réaffiché. « Mot de passe illisible : ressaisissez-le. » veut dire qu'il faut le retaper.",
      },
      {
        id: 'qui-recoit-quoi',
        title: 'Choisir qui reçoit quelle alerte',
        who: 'Administrateur',
        where: 'Notifications e-mail → Qui reçoit quoi',
        steps: [
          "Catégories : « Nouvelles commandes », « Paiements reçus », « Candidatures », « Demandes de table », « Commandes à table (soirée) », « Demandes RGPD », « Changements côté admin », « Sécurité des comptes ».",
          "« Ajouter une adresse » : « Adresse e-mail », « Nom (facultatif) », « Langue des e-mails ».",
          "« Tester » sur la ligne. « Suspendre » / « Réactiver » coupe une adresse sans la supprimer.",
        ],
        why: "Les abonnements valent pour toutes les éditions. Sans adresse, rien ne part.",
      },
      {
        id: 'messages',
        title: 'Relire les e-mails envoyés aux clients',
        who: 'Administrateur, Responsable événement',
        where: 'Messages',
        steps: [
          'Choisir la langue (FR, DE, EN) et ouvrir un modèle.',
          "Modifier « Objet » et « Message » (texte simple), « Enregistrer » : le badge devient « Votre version ».",
          "« Variables disponibles » (replié sous les champs sur téléphone) : les variables que CE message reçoit, avec leur sens et un exemple. Cliquer dans « Objet » ou « Message », puis « Insérer » écrit la variable au curseur ; « Copier » la met dans le presse-papiers.",
          "Une variable inconnue est surlignée en rouge, avec l'avertissement « Variable inconnue : elle apparaîtra telle quelle dans l'e-mail. » — corriger son nom avant d'enregistrer.",
          "« Aperçu » : le texte en cours, rendu avec des valeurs d'exemple (version e-mail puis version texte). Rien n'est envoyé ni enregistré.",
          "« Revenir au modèle d'origine » annule la modification.",
          "Messages de commande : {{order_heading}} (ce qui a été acheté), {{order_details}} (une partie par famille), {{cancel_details}} (ce que l'annulation change), {{pay_on_site_note}} (règlement sur place) suivent la commande réelle.",
        ],
        why: "Les modèles d'origine sont remis à jour à chaque déploiement ; « Votre version » d'une édition n'est jamais écrasée — elle doit donc garder ces mots pour rester juste.",
        warning: "Les mots entre {{…}} sont remplacés à l'envoi ; une variable absente du panneau « Variables disponibles » reste visible telle quelle dans l'e-mail.",
      },
      {
        id: 'textes-des-variables',
        title: 'Réécrire les phrases des variables',
        who: 'Administrateur, Responsable événement (droit « admin.notification.update »)',
        where: 'Messages → Textes des variables',
        steps: [
          "Onglet « Textes des variables » en haut de Messages — ou « Modifier ses phrases » sous une variable composée ({{order_details}}, {{practical_info}}…) dans « Variables disponibles ».",
          'Choisir la langue (FR, DE, EN), ouvrir un groupe : « Composition de la commande », « Confirmation de table », « Suppléments », « Habillage des e-mails », « Règlement », « Tables sur mesure et personnalisées ».',
          "Chaque phrase dit quand elle est utilisée (« Plats d'une commande SANS table : retrait au comptoir. »). « Modifier », écrire le texte, « Enregistrer pour cette édition ».",
          "Les valeurs s'écrivent avec UNE accolade : {number}, {amount}, {time}… Les boutons sous le champ les insèrent ; « obligatoire » ne peut pas être retiré.",
          "« Aperçu avec des valeurs d'exemple » suit la saisie ({time} = l'heure d'ouverture réelle de l'édition). L'aperçu des modèles montre la phrase aussitôt enregistrée.",
          "« Revenir au texte de la plateforme » supprime votre version.",
        ],
        why: "Ces phrases composent les variables que le système remplit lui-même : infos pratiques d'une table, « À régler à l'entrée : … », retrait au comptoir ou au bar, pied de page des e-mails. Le texte de la plateforme est mis à jour à chaque déploiement ; votre version n'est jamais écrasée.",
        warning: "Refusé avec le motif : une valeur inconnue, une valeur obligatoire retirée, une accolade seule ou {{…}}, un texte vide (sauf masque, accueil par l'hôtesse, légendes du QR et pied de page, qui peuvent disparaître). Les heures ne s'écrivent jamais en dur : elles viennent d'Événement.",
      },
      {
        id: 'contenus',
        title: 'Écrire le site public',
        who: 'Administrateur, Responsable événement',
        where: 'Contenus',
        steps: [
          "« Blocs éditoriaux » : « Clé », « Titre », « Sur-titre », « Texte », « Libellé du bouton », « Lien du bouton », « Visible ». Les deux premiers vont sur l'accueil, les autres dans Infos.",
          "Chaque texte se saisit dans les trois langues ; « N à compléter » dit ce qui manque.",
          "« Questions fréquentes » (page Infos, dans l'ordre de la liste) : « Ajouter une question » → « Question », « Réponse », « Catégorie (facultatif) », « Visible sur le site ».",
          "Les « Partenaires », « L'équipe LeYAMO » (les visages du site, différents des comptes).",
          "Textes à place fixe (blocs « site.* », créés au déploiement) : site.decor (titre et légende sous le visuel du décor, accueil), site.competition (titre de la section concours), site.info_intro et site.team_intro (introductions des pages Infos et L'équipe). Vides ou masqués, ils disparaissent du site.",
        ],
      },
    ],
  },
  {
    id: 'catalogue',
    icon: '🍾',
    phase: 'preparer',
    title: 'Billets, carte, boissons et tables',
    intro:
      "Ce qu'on vend. L'ordre conseillé : billets (avec l'« Entrée table » à 0 €), carte, boissons, types de tables, plan de salle.",
    procedures: [
      {
        id: 'billets',
        title: "Créer les types de billets",
        who: 'Administrateur, Responsable événement',
        where: 'Billets → Types de billets → Ajouter un type',
        steps: [
          "« Code » en majuscules (SOLO, COUPLE) — il ne change plus ensuite.",
          "« Nom », « Description », « Prix », « Personnes admises » (1 individuel, 2 couple — la commande reçoit un seul QR, quel que soit ce nombre), « Quota » (vide = sans limite), « Maximum par commande ».",
          "« Pack restauration inclus » (« Aucun » par défaut) ; « Début de la vente » / « Fin de la vente » (vides = tout de suite / jusqu'à la fermeture de la vente ; avant le début, « En vente le … ») ; « Avantages » (un par ligne, sous le billet) ; « Photo ».",
          "« En vente » coché. La ligne affiche ensuite « N personnes · X / Y vendus ».",
          "Créer aussi « Entrée table » : 0 €, « En vente » DÉCOCHÉ, et recopier son code dans Réglages.",
        ],
        warning:
          "Les entrées vendues à la porte consomment le même quota : la porte n'est jamais refusée, mais le site s'arrête au quota. Un billet déjà vendu ne se supprime pas, il se désactive.",
      },
      {
        id: 'regles-prix',
        title: 'Créer une remise ou un code promo',
        who: 'Administrateur, Responsable événement',
        where: 'Billets → Règles de prix → Ajouter une règle',
        steps: [
          "« Nom », « Portée » (Commande entière, Billets, Plats, Packs, Boissons, Tables), « Nature » (Remise %, Remise montant, Supplément %, Supplément montant, Prix imposé), « Pourcentage » ou « Montant ».",
          "« Code promo » : vide, la règle s'applique toute seule ; rempli, le client doit le taper.",
          "« Utilisations maximum », « Priorité » (la plus petite d'abord), « Cumulable », « En vente ».",
          "Ouvrir le réglage « Proposer un code promo » pour que le champ apparaisse au client.",
        ],
      },
      {
        id: 'carte',
        title: 'Saisir la carte',
        who: 'Administrateur, Responsable événement',
        where: 'Carte',
        steps: [
          "« Catégories » : « Nom », « Description », « Visible », flèches pour l'ordre.",
          "« Ajouter un plat » : « Nom », « Prix », « Prix d'achat », « Poste de préparation » (Cuisine / Bar / Aucun — l'écran où il arrive le soir J), « Stock » (vide = illimité, 0 = épuisé), « Maximum par commande », « Disponible », « En vente », « Photo ».",
          "« Ajouter un pack » : « Prix », « Prix d'achat », « Stock » (vide = illimité), « Maximum par commande » (vide = sans limite), « Donne l'entrée » (non par défaut), « En vente », « Photo », « Composition » (au moins un plat).",
        ],
        warning: "Un plat vendu ou présent dans un pack ne se supprime pas : « Désactivez-le plutôt. » Sur /carte, un plat dont « Disponible » est décoché porte « Indisponible » ; un plat sans stock ou marqué épuisé le soir J porte « Épuisé ».",
      },
      {
        id: 'boissons-tables',
        title: 'Saisir les boissons et les types de tables',
        who: 'Administrateur, Responsable événement',
        where: 'Boissons & tables',
        steps: [
          "« Ajouter une boisson » : « Nom », « Catégorie », « Marque », « Format », « Prix », « Prix d'achat », « Stock », « Ne compte pas dans la franchise » (softs, eau, mixers), « Vendue uniquement sur commande », « En vente », « Photo ».",
          "« Ajouter un type de table » : capacités, « Nombre de tables en vente » (c'est la limite de vente), « Mode de tarification », « Prix de base », « Montant minimum », « Donne l'entrée » (décoché par défaut), « Photo de la table ». « Bouteilles comprises » et « Bouteilles comprises d'office » ne se règlent qu'en mode « Bouteilles comprises, le reste facturé » ou « Composition libre » ; sinon l'écran le dit.",
          "Bloc « Formules de table » → « Ajouter une formule » (après au moins un type de table) : « Nom », « Type de table » (fixé à la création), « Prix de la formule » (remplace le prix de base), « Nombre maximum » (vide = sans limite), « Composition » (au moins une bouteille). Les bouteilles en plus restent facturées.",
        ],
      },
      {
        id: 'plan-salle',
        title: 'Créer le plan de salle',
        who: 'Administrateur, Responsable événement',
        where: 'Boissons & tables → Plan de salle',
        needs: 'Le plan de la salle avec les numéros de table',
        steps: [
          "« Ajouter une table » : « Numéro » (obligatoire, unique — c'est lui qui est dans le QR), « Nom de la table » (« Champagne »), « Type », « Zone », « Couverts », « État ».",
          "Le nom s'affiche partout avec le numéro : à la porte, chez le serveur, en cuisine, au bar, sur le QR.",
        ],
        why: "Le plan de salle sert au placement et aux QR ; ce qui limite la vente, c'est « Nombre de tables en vente » du type de table.",
      },
      {
        id: 'qr-codes',
        title: 'Imprimer les QR et copier les liens',
        who: 'Administrateur, Responsable événement (toute personne qui lit le plan de salle ou l\'édition)',
        where: 'QR & liens (/admin/qr-codes)',
        needs: 'Une imprimante, du papier épais',
        steps: [
          "« Dans la salle » : « Voter » → /vote (un seul QR pour toute la salle) et « Commander (sans table) » → /commander. À afficher au mur, au bar, à l'entrée.",
          "« Le site public » : un QR par page publique (badge « Suspendue » si la page est coupée). À partager librement.",
          "« Écrans de l'équipe » : « Porte — entrées » (/ops/checkin), « Salle — service et placement » (/ops/service), « Cuisine » (/ops/cuisine), « Bar » (/ops/bar), « Caisse » (/ops/pos), « Déroulé — maître de cérémonie » (/ops/deroule), « Régie en direct » (/ops/live), « Bilan » (/ops/bilan), « Stock » (/ops/stock), et « Encaisser (redirige selon le rôle) » (/ops/caisse). Chaque carte dit à qui elle s'adresse et liste les « Rôles : … » qui l'ouvrent.",
          "« Back-office » : /admin.",
          "« Liens des jurés » (badge « Personnel — ne pas afficher en public ») : visible seulement avec le droit competition.category.update. « Écran de la salle » (badge « Confidentiel ») : visible seulement avec le droit event.settings.update.",
          "« Les tables » : un QR par table, « Table 12 · Champagne » → /commander?table=12, numéro pré-rempli.",
          "« Copier le lien » sous une carte pour l'envoyer par message (« Lien copié »).",
          "Cocher les cartes voulues, une à une ou avec « Sélectionner la rubrique », puis « Imprimer N QR » : une page par QR, avec son titre, à qui il s'adresse et l'adresse en toutes lettres.",
          "Scanner un QR de table avec un téléphone pour vérifier numéro et nom. Imprimer la veille au plus tard.",
        ],
        warning:
          "Rien de coché = tout s'imprime, SAUF les liens confidentiels (jurés, écran de la salle) : ceux-là ne partent sur papier que cochés. Un lien de juré donne le pouvoir de noter, celui de l'écran affiche l'écran à n'importe qui : à remettre à la personne seule, jamais à afficher.",
        why: "Un écran d'équipe demande toujours une connexion et le droit du poste : son QR évite seulement de taper l'adresse.",
      },
      {
        id: 'ecrans-equipe',
        title: "Donner son écran à chaque membre de l'équipe",
        who: 'Administrateur, Responsable événement',
        where: "QR & liens → « Écrans de l'équipe »",
        needs: "Les comptes de l'équipe créés, avec leur rôle",
        steps: [
          "Repérer la carte de chaque poste : elle dit à qui elle s'adresse et quels « Rôles : … » l'ouvrent.",
          "Soit imprimer les QR des postes (cocher, ou « Sélectionner la rubrique » → « Imprimer N QR ») et poser chaque feuille à son poste : porte, bar, cuisine, caisses.",
          "Soit « Copier le lien » et l'envoyer à chacun selon son rôle : porte /ops/checkin, serveur et hôtesse /ops/service, cuisine /ops/cuisine, bar /ops/bar, caissier /ops/pos, maître de cérémonie /ops/deroule, réserve /ops/stock. Dans le doute : « Encaisser (redirige selon le rôle) ».",
          "Chacun l'ouvre une fois avant le soir J et se connecte avec son propre compte.",
        ],
        warning: "Les liens des jurés et de l'écran de la salle ne se distribuent pas ainsi : en main propre seulement.",
      },
    ],
  },
  {
    id: 'postes-preparation',
    icon: '🧾',
    phase: 'preparer',
    title: 'Caisses, serveurs et programme',
    intro: "Ce qui doit être prêt pour que le soir J tourne : une caisse par poste, un serveur par table, un programme qui conduit le concours.",
    procedures: [
      {
        id: 'caisses',
        title: 'Créer une caisse par poste',
        who: 'Administrateur, Responsable événement',
        where: 'Finances → Les caisses → Ajouter une caisse',
        steps: [
          "« Code » (BAR1, CUISINE1, ENTREE), « Nom », « Emplacement ».",
          "« Poste servi » : « Bar » (vend les boissons), « Cuisine » (les plats), « Entrée (porte) » (ne vend rien au comptoir ; reçoit les entrées et les commandes payées à la porte), « Tout le catalogue ».",
          "« Créer la caisse ». Il en faut au moins une Bar, une Cuisine, une Entrée.",
        ],
        warning: "Une caisse qui a déjà servi ne se supprime pas : « Désactivez-la plutôt ». Une caisse = une personne à la fois. Le soir J, le Bar ouvre la caisse Bar, la Cuisine la caisse Cuisine, la porte la caisse Entrée ; le rôle Caisse peut prendre n'importe laquelle.",
      },
      {
        id: 'tables-serveurs',
        title: 'Confier chaque table à un serveur',
        who: 'Responsable de salle (service.table.assign) : Administrateur, Responsable événement',
        where: 'Service en salle → Les tables et leur serveur',
        needs: 'Des comptes avec le rôle Service en salle, un plan de salle',
        steps: [
          "Pour chaque table, choisir son serveur. Une table a un serveur à la fois ; un serveur, autant de tables qu'on veut.",
          "Vérifier le résumé « Par serveur ».",
        ],
        why: "Une table sans serveur n'est pas oubliée : ses commandes s'affichent chez tous les serveurs. Changer le serveur d'une table ne déplace pas les commandes déjà passées.",
      },
      {
        id: 'placement-avance',
        title: "Attribuer les tables aux réservations, d'avance",
        who: 'Administrateur, Responsable événement (tables.placement.update) — ou une hôtesse qui a accès au back-office',
        where: 'Placement (/admin/placement)',
        needs: 'Le plan de salle saisi (Boissons & tables → Plan de salle), des réservations de table payées',
        steps: [
          "La page liste chaque réservation de table payée : le nom de l'acheteur, la formule, le nombre de personnes, la table, l'arrivée, l'installation.",
          "Filtres : « Toutes », « Arrivés » (passés à la porte, pas encore installés), « À placer » (pas encore arrivés, sans table), « Placés » (pas encore arrivés, table attribuée), « Installés » ; la recherche trouve un nom, un numéro de commande ou une table.",
          "Toucher une ligne (ou « Attribuer une table » sur téléphone) : les tables libres du bon type d'abord, puis « Autres tables ». Une table déjà donnée à un autre groupe n'est pas proposée.",
          "Se raviser : « Changer de table », ou « Retirer la table ». Sans motif tant que le groupe n'est pas installé.",
          "« Historique » : qui a donné, changé, installé — et pourquoi.",
        ],
        warning: "Une table où un groupe est installé est bloquée : ni cette page, ni la porte, ni l'écran Salle ne peuvent la donner à un autre groupe, et le plan de salle refuse de la passer hors service.",
        why: "Attribuer d'avance ne déclare pas le groupe arrivé : l'arrivée vient toujours du scan à la porte.",
      },
      {
        id: 'programme',
        title: 'Construire le programme',
        who: 'Administrateur, Responsable événement',
        where: 'Programme',
        steps: [
          "« Ajouter une étape » : « Titre », « Nature » (Créneau, Animation, Tour de concours, Cake Moment, Remise de prix, Libre), « Début », « Fin », « Lieu », « Animateur ».",
          "Une Animation exige son « Animation liée » (créée sous « Animations »).",
          "« Visible du public » décoché : l'étape n'apparaît qu'au conducteur.",
          "Pour le concours : une étape « Tour de concours » par tour.",
        ],
        why: "Le maître de cérémonie peut aussi retoucher ce même programme depuis son conducteur (/ops/deroule → onglet « Programme » : heures, durées, libellés, ordre, étapes ajoutées ou retirées, « Décaler la suite »). Le lien d'une étape avec un tour ou le Cake Moment, lui, se règle ici.",
      },
      {
        id: 'cake-moment',
        title: 'Préparer le LeYAMO Cake Moment',
        who: 'Administrateur, Responsable événement (program.cake.update)',
        where: 'Programme → 🎂 Cake Moment',
        steps: [
          "« Titre », « Message à l'écran » (le grand texte en salle), « Description », « Visuel ».",
          "« Étape qui le déclenche » (facultatif : « Toute étape « Cake Moment » »), « Heure prévue » (heure de l'édition, annoncée sur le site).",
          "« Activé » coché, « Notes internes » (qui apporte le gâteau, bougies…) → « Enregistrer ».",
        ],
        why: "Quand le maître de cérémonie démarre l'étape « Cake Moment » au conducteur, l'écran de la salle bascule sur ce moment. Il faut donc aussi une étape de nature « Cake Moment » au programme.",
      },
      {
        id: 'ecran-salle',
        title: "Préparer l'écran de la salle",
        who: 'Administrateur, Responsable événement (event.settings.update)',
        where: 'Programme → carte « Écran de la salle »',
        steps: [
          "La carte affiche l'adresse complète de l'écran, clé comprise : « Copier l'adresse », ou « Ouvrir l'écran ».",
          "L'ouvrir la veille sur l'appareil de la salle. Sans clé dans l'adresse : « Écran non configuré ».",
          "Une seule vue à la fois ; la première vraie l'emporte : 1) ce que le maître de cérémonie pose à l'écran : pour le pronostic, le profil public complet de chaque candidat (Miss à gauche, Master à droite : photo, « N° 2 », nom de scène, âge, activité, études, les trois mots, l'adage, puis sa présentation dans la langue de l'écran, coupée par « … » si elle est longue ; jusqu'à 8 par concours sur une page, au-delà des pages « 1 / 2 » qui tournent toutes les 12 s) ; ou un gagnant désigné ; ou une question du quiz (« Question 3 / 10 », la question en très grand, quatre cases A à D en 2 × 2 ; aucune trace de la bonne réponse avant « Révéler la réponse », puis la bonne case s'allume en or avec ✓ et les autres s'éteignent) ; 2) le Cake Moment pendant son étape ; 3) les scores publiés, tout sur une seule page, sans rotation : en haut, sous une guirlande, « Vainqueur · Miss », « Vainqueur · Master » avec photo, numéro et nom ; dessous, un tableau par concours côte à côte — rang, photo, nom, chaque tour sur 100 (« P » public sur 70, « J » jury sur 30 ; ce détail se retire au-delà de 8 candidats par concours, les lignes se resserrent, la page ne se découpe jamais), total sur 300, vainqueur encadré d'or avec 👑, mention « départagé·e par le jury » ou « au vote de départage » ; retiré dès qu'on annule la publication ; 4) un vote ouvert : « Le vote est ouvert », les nommés en photo avec numéro et nom (aucun bouton, aucun compteur de voix) et le décompte en très grands chiffres jusqu'à « 0:00 — Temps écoulé » ; 5) le défilé : le programme à gauche et, à droite, le profil de la personne que le maître de cérémonie a mise à l'écran (photo, « N° 2 », « Sur le podium · Miss », le tour, l'âge, l'activité, les études, l'adage, « En trois mots ») ; 6) la proclamation, dès qu'un titre est proclamé et tant que ses scores ne sont pas publiés : une carte par titre, côte à côte sur une page (Miss, puis Miss et Master), chacune sous sa guirlande, « Vainqueur · Miss » en grand, la photo, le numéro et le nom en très grands caractères — jamais un score ; 7) le programme : l'étape en cours, la suivante, plus tard, le départage en cours.",
          "Pensé pour une télé 1920×1080 lue à 10 m, sans défilement ; tient aussi en 1280×720 et sur une tablette. Relu toutes les 15 s, toutes les 5 s pendant un tour, un vote ouvert ou un affichage du maître de cérémonie. Jamais le nom civil, l'e-mail ni le téléphone d'un candidat.",
        ],
        warning:
          "L'adresse contient la clé : ne la donner qu'à qui branche la télé (elle est aussi dans QR & liens → « Écran de la salle », badge « Confidentiel », imprimée seulement si cochée). Si la carte dit « Aucune clé d'écran n'est configurée sur le serveur (SCREEN_ACCESS_KEY) », le technicien doit la définir.",
      },
    ],
  },
  {
    id: 'concours-preparation',
    icon: '👑',
    phase: 'preparer',
    title: 'Le concours Miss & Master',
    intro:
      "Trois choses : la catégorie (Miss, Master), le tour (3 par catégorie), et le vote d'un tour, où le public vote et le jury note. Chaque tour est noté sur 100 : 70 pour la part des voix du public, 30 pour la part des points du jury. Le titre se joue sur le cumul des trois tours (sur 300).",
    procedures: [
      {
        id: 'concours-categories',
        title: 'Ouvrir les candidatures',
        who: 'Administrateur, Responsable événement',
        where: 'Miss & Master → Catégories',
        steps: [
          "« Ajouter une catégorie » : « Code » (MISS, MASTER), « Nom », « Description », « Règlement », « Places » (vide = sans limite).",
          "La case active (libellée « En vente ») cochée = candidatures possibles sur /concours ; décochée = fermées.",
          "« Ouverture des candidatures » / « Clôture des candidatures » : à l'heure de la salle (le fuseau de l'édition est rappelé). Le formulaire public s'ouvre et se ferme seul ; vide = sans limite.",
        ],
        why: "L'étape 9 de la Préparation (« Ouvrir les candidatures ») ne compte que les catégories qui ont une date d'ouverture.",
      },
      {
        id: 'candidatures',
        title: 'Valider ou refuser une candidature',
        who: 'Administrateur, Responsable événement',
        where: 'Miss & Master → Candidatures',
        steps: [
          "Ouvrir la candidature ; vérifier la « Photo du candidat » (on peut la changer, elle s'enregistre tout de suite).",
          "Relire le bloc « Profil public » (âge, sport ou activité, niveau d'études, adage, trois mots) : c'est ce que la salle lira sur /candidats. Le corriger au besoin (procédure suivante).",
          "« Dossard » : vide = le prochain numéro libre de la catégorie (Miss et Master commencent chacun à 1).",
          "« Approuver » : la personne reçoit « Vous êtes en lice — dossard n° X » et apparaît sur /candidats.",
          "« Refuser » : « Motif » obligatoire, il part dans l'e-mail ; le dossard est libéré.",
        ],
        warning: "Une fois approuvé, le dossard ne se change plus depuis l'écran. Ne refusez pas un candidat déjà nommé dans un vote.",
      },
      {
        id: 'profil-candidat',
        title: 'Compléter ou corriger le profil public d\'un candidat',
        who: 'Administrateur, Responsable événement (competition.candidate.update)',
        where: 'Miss & Master → Candidatures → le candidat → bloc « Profil public »',
        steps: [
          "Au dépôt (/concours), le bloc « Votre profil public » est obligatoire : « Âge » (16 à 120 ans), « Sport ou activité pratiqué(e) », « Niveau d'études » (texte libre, avec des suggestions : Lycée / Bac / Abitur, Formation professionnelle / Ausbildung, Bachelor / Licence, Master, Doctorat, Autre), « Un adage qui vous ressemble », « Trois mots qui vous décrivent » (trois mots ou courtes expressions différents, 30 caractères au plus chacun). Le formulaire prévient qu'ils apparaîtront sur sa fiche publique.",
          "Côté équipe : ouvrir la candidature → « Profil public » → compléter ou corriger « Âge », « Sport ou activité pratiqué(e) », « Niveau d'études », « Un adage qui vous ressemble », « Mot 1 », « Mot 2 », « Mot 3 ».",
          "« Enregistrer le profil » → « Profil enregistré. »",
          "Une candidature déposée avant ce formulaire n'a pas de profil : c'est ici qu'on le complète.",
        ],
        warning:
          "Un champ vidé disparaît du profil public. Trois mots différents, ou aucun. Sans le droit de modifier, le bloc se lit seulement.",
        why: "Dès que la candidature est validée, /candidats affiche, à côté du nom de scène (ou du prénom et de l'initiale), l'âge, le sport ou l'activité, « Études : … », l'adage et « En trois mots » — jamais le nom de famille, l'e-mail ni le téléphone. Le profil fait partie de l'export RGPD de la personne et s'efface avec sa candidature.",
      },
      {
        id: 'concours-tours',
        title: 'Créer les tours',
        who: 'Administrateur, Responsable événement',
        where: 'Miss & Master → Les tours',
        steps: [
          "« Ajouter un tour » : « Nom du tour », « Concours » (Miss ou Master), « Étape du déroulé ».",
          "Rattacher les tours Miss et Master d'un même passage à la même étape : lancer l'étape au conducteur les démarre ensemble, la terminer les clôt.",
        ],
      },
      {
        id: 'scrutins',
        title: 'Créer un vote par tour',
        who: 'Administrateur, Responsable événement',
        where: 'Votes → Ajouter une catégorie',
        steps: [
          "« Concours » (Miss ou Master), puis « Tour » : le premier tour sans vote est proposé d'office. Un vote de concours a toujours son tour — sans tour, il est refusé. « Nom ».",
          "Un vote isolé (« Best Mask ») : « Concours » = « Aucun (scrutin isolé) », le tour reste grisé sur « Aucun tour » et son score ne compte dans aucun titre.",
          "« Voix par personne » (1), « Vote modifiable » (non).",
          "« Réservé aux présents » : coché, seuls les billets scannés votent (lien personnel) ; DÉCOCHÉ, la salle vote aussi avec le QR « Voter » des tables.",
          "Facultatif : « Ouverture automatique au plus tôt » et « Fermeture automatique », à l'heure de la salle — un garde-fou : le vote n'est ouvert que si son état est « ouvert » ET qu'on est dans la fenêtre.",
          "Enregistrer, rouvrir → « Nommés » : cocher les candidats → « Enregistrer les nommés ».",
          "Laisser le vote fermé : c'est le maître de cérémonie qui l'ouvre.",
        ],
        warning:
          "Un vote rattaché au concours Master ne propose que les candidats Master (un vote Miss, que les Miss) ; un vote neuf doit d'abord être enregistré pour que la liste se remplisse. Le tour doit être un tour de CE concours ; repasser un vote à « Aucun » concours lui retire son tour. Nommés, voix et « Réservé aux présents » se figent à l'ouverture.",
      },
      {
        id: 'jury',
        title: 'Composer le jury de chaque concours',
        who: 'Administrateur, Responsable événement ; le Maître de cérémonie aussi, le soir (competition.jury.manage)',
        where: 'Miss & Master → Le jury (ou conducteur /ops/deroule → onglet « Jury », mêmes boutons)',
        needs: "Le nom des trois ou quatre jurés, leur e-mail si possible",
        steps: [
          "« Nom », « E-mail » (facultatif), « Langue » → « Ajouter un juré ».",
          "Donner son lien personnel, sans compte : « Envoyer le lien » (par e-mail, dans sa langue) ou « Afficher le QR » (il le scanne). Les mêmes liens sont aussi dans QR & liens → « Liens des jurés ».",
          "Chaque juré ouvre son lien avant le soir J et voit « Jury · Miss » et son nom.",
          "Corriger un nom, une adresse ou une langue : « Modifier » sur la ligne du juré → « Enregistrer ». Le lien et les notes restent ; sa page et « Qui a noté quoi » affichent le nouveau nom.",
        ],
        warning:
          "Lien perdu : « Nouveau lien » — l'ancien ne vaut plus rien, les notes restent. « Révoquer » coupe le lien. « Retirer » n'existe que pour un juré sans note. Sans juré, la part « jury » (30 %) vaut 0.",
      },
      {
        id: 'recompenses',
        title: 'Créer un prix et révéler son podium',
        who: 'Administrateur, Responsable événement (créer) ; Maître de cérémonie aussi pour saisir le podium et le révéler (competition.award.update, competition.result.reveal)',
        where: 'Miss & Master → Récompenses (ou conducteur /ops/deroule → onglet « Concours » → « Prix et podiums » → « Ouvrir le podium »)',
        steps: [
          "« Ajouter un prix » : « Nom du prix » (« Prix du public », « Best Mask »…), « Ce qu'on gagne », « Concours » (ou « Tous les candidats »), « Visuel ».",
          "Rouvrir le prix → « Podium » : par rang, un « Candidat » ou « Ou un nom libre » → « Enregistrer le podium ». Badge « Podium saisi » : c'est toujours privé.",
          "Au moment voulu : « Révéler le podium » → « Révéler maintenant ». Badge « Révélé ».",
        ],
        warning:
          "Révélé, le podium est public sur le site et ne se modifie plus, et le premier rang passe « vainqueur » dans son dossier de candidat. Un prix révélé ne se supprime plus. Révélé par erreur : « Masquer à nouveau » (motif obligatoire, même droit que révéler) — le podium redevient privé et modifiable, le premier retrouve son statut d'avant (sauf autre titre).",
        why: "Le titre Miss / Master, lui, se proclame dans le tableau privé (Votes, ou conducteur → onglet « Concours »).",
      },
    ],
  },
  {
    id: 'vente-en-ligne',
    icon: '🛒',
    phase: 'preparer',
    title: 'La vente en ligne',
    intro:
      "Le client commande seul ; l'équipe envoie le lien de paiement, constate les paiements, gère les tranches, annule et rembourse. Le système ne voit pas les comptes PayPal : un paiement par lien se constate toujours à la main.",
    notes: [
      {
        title: 'Ce que fait le client',
        body:
          "Il choisit sur /billets ou depuis /carte (« Réserver cette table » : type, nombre de personnes, bouteilles, extras, plats). Il donne ses coordonnées — prénom, nom, e-mail obligatoires, numéro WhatsApp facultatif (au format international, +49…) — et le seul prénom de chaque accompagnant, puis « Comment souhaitez-vous régler ? » : « En ligne par PayPal » (un lien et une référence ENT01, TBL02…), « En espèces à l'entrée », « Par PayPal à l'entrée ». Pour une table, si c'est permis : « Tout payer maintenant » ou « Réserver avec X maintenant, puis Y ». Le stock est retenu dès la commande.",
      },
      {
        title: 'Plats et bouteilles en ligne : il faut une entrée',
        body:
          "Une commande en ligne avec des plats ou des bouteilles pré-commandées n'est acceptée que si la même commande contient une entrée (billet, table, ou pack repas qui « Donne l'entrée »), ou si le client indique le numéro d'une commande d'entrée de la même édition, passée avec la même adresse e-mail, et réglée, avec acompte, partiellement remboursée ou « à régler à l'entrée » (une entrée qui attend son paiement en ligne doit d'abord être réglée). À l'étape « Vos coordonnées » : « Vous avez déjà votre billet ou votre table ? » → « Numéro de votre commande d'entrée » → « Vérifier », ou « Ajouter un billet d'entrée » / « Réserver une table ». Refus possibles : « Aucune commande d'entrée ne correspond à ce numéro et à cette adresse e-mail. » (numéro inconnu, autre édition ou autre adresse : même message), « … n'est plus valable (annulée, expirée ou remboursée). », « … n'est pas encore réglée : réglez-la d'abord… », « Cette commande ne contient ni billet d'entrée ni table. ». Le bouton « Commander à manger » (page de la commande, Mon Masked Night) remplit le numéro dès que l'entrée est réglée ou à régler à l'entrée. Un seul numéro par réservation : les plats sont AJOUTÉS à la commande citée (« Ces plats seront ajoutés à votre commande MN26-… : même numéro, même code d'entrée (QR). »), sous forme de supplément — même numéro, même QR, aucune seconde commande. Le supplément a son propre règlement : « Supplément à régler : X », sa référence ESS…, son lien PayPal ou « À régler sur place », son échéance ; sans règlement à temps, seul le supplément est annulé, jamais la réservation. Une table payée reste payée et entre à la porte ; la cuisine et le bar ne voient les plats d'un supplément qu'une fois ce supplément réglé. Les anciennes commandes de plats (avant les suppléments) gardent « Liée à la commande d'entrée » / « Repas rattachés » jusqu'à leur fusion. Si l'entrée est annulée depuis sa fiche, la case « Annuler aussi les repas rattachés non réglés » (cochée d'office) les annule avec elle — le client reçoit l'e-mail d'annulation ; les repas réglés restent, « réglée — à rembourser séparément ». Si l'entrée tombe sans personne devant l'écran (échéance non réglée, panier expiré, remboursement intégral, suppression), les repas non réglés sont annulés d'office et les réglés signalés à la trésorerie (alerte « Paiements »). Sans table, plats au comptoir et bouteilles au bar en annonçant le numéro [à confirmer par Ludovic]. La commande dans la salle le soir J (/commander, caisse, serveurs) n'est pas concernée.",
      },
      {
        title: 'Ce que reçoit le client',
        body:
          "La page de sa commande (/commande/…) : « Référence à indiquer », « À régler maintenant : X. Au plus tard le … », les tranches à venir, « Payer maintenant », les consignes, et le lien « Ouvrir Mon Masked Night » vers « Mon billet » (/mon-billet) et son QR — non payé, il « n'ouvrira la porte qu'une fois le règlement confirmé ». Chaque e-mail de commande décrit exactement ce qui a été acheté : une partie par famille (entrée avec la ligne du QR, table avec formule, convives et entrée comprise ou non, repas pré-commandé, bouteilles) ; le mot « QR » seulement s'il y a une entrée ; sans entrée, le bouton est « Ouvrir ma commande ». Par e-mail : « Règlement de votre commande… » quand l'équipe envoie le lien (total, montant à régler maintenant et date limite, référence, échéancier des tranches, date d'annulation automatique) ; pour un paiement à l'entrée, le billet tout de suite ; la confirmation avec les QR au paiement complet ; « Votre commande … est annulée » (avec le motif) si l'équipe l'annule ; les rappels, relances et annulations sans acompte, la nuit.",
      },
    ],
    procedures: [
      {
        id: 'envoyer-lien',
        title: 'Envoyer le lien de paiement',
        who: 'Administrateur, Responsable événement, Équipe',
        where: 'Commandes → ouvrir la commande',
        steps: [
          "Relire la commande (nombre de personnes, adresse, doublon).",
          "« Envoyer le lien » (ou « Renvoyer le lien »). Pas pour une commande payée à l'entrée.",
          "Une commande expirée peut recevoir un nouveau lien si le stock est encore libre.",
        ],
      },
      {
        id: 'constater-paiement',
        title: 'Constater un paiement reçu',
        who: 'Administrateur, Responsable événement, Équipe',
        where: 'Commandes → ouvrir la commande → Encaisser hors ligne',
        steps: [
          "Vérifier dans le compte PayPal (ou sur le relevé) que l'argent est arrivé, avec la référence de la commande.",
          "« Moyen » (Espèces, Virement, Autre (manuel)), « Référence » (celle du paiement PayPal), « Marquer comme encaissée ».",
          "Les billets sont émis. Sans table, la confirmation part avec les QR, comme avant. Avec une table (en ligne ou sur mesure), rien ne part tout seul : en haut de la liste, « Table réglée (N°). Envoyer la confirmation maintenant ? » → « Envoyer maintenant ».",
          "Une tranche : bloc « Échéancier » → « Tranche encaissée ». Les billets partent avec la dernière tranche. Dès le premier acompte, une table est au placement (« Solde à encaisser »).",
        ],
        warning:
          "Encaisser une table hors ligne n'envoie plus le message générique (sans noms, sans bouteilles, souvent sans QR) : la confirmation complète part du bouton. Un paiement PayPal arrivé tout seul envoie toujours sa confirmation automatique.",
      },
      {
        id: 'envoyer-confirmation',
        title: 'Envoyer ou renvoyer la confirmation d\'une commande réglée (ou d\'une table réglée à l\'entrée)',
        who: 'Administrateur, Responsable événement, Équipe (droit « Encaisser hors ligne »)',
        where: 'Commandes → ouvrir la commande → bloc « Confirmation au client »',
        steps: [
          "Commande avec une table : « Envoyer la confirmation de réservation » — remerciements, table (type, formule, nom du groupe), personnes, chaque convive, les bouteilles de la table, infos pratiques et le QR de la réservation (émis s'il manquait).",
          "Autre commande : « Envoyer la confirmation » — la confirmation du règlement, avec son QR.",
          "Ensuite « Renvoyer… », avec « Confirmation envoyée le … par … ». Le badge « Confirmation à envoyer » signale dans la liste une table réglée dont la confirmation n'est pas partie.",
          "Une table sur mesure a le même bouton dans le bloc de sa demande, sur la même fiche.",
          "Table réglée à l'entrée (badge « À régler à l'entrée : X € ») : le bouton est là avant l'encaissement. Même confirmation (table, convives, bouteilles, QR), avec en gras « À régler à l'entrée : X € (espèces, carte ou PayPal). ».",
        ],
        warning:
          "Sinon, seulement une commande réglée : un acompte ne suffit pas (« La commande n'est pas réglée… »). Le résultat dit si le message est parti, retenu (hors production) ou refusé par le serveur d'envoi ; chaque envoi est au journal.",
      },
      {
        id: 'definir-type-table',
        title: 'Une table absente du placement : définir son type',
        who: 'Administrateur, Responsable événement (droit « Modifier une réservation »)',
        where: 'Commandes → ouvrir la commande (badge « Table sans type »)',
        steps: [
          "Bandeau « Cette commande contient une table sans réservation… » : choisir le « Type de table » (« Sur mesure » d'office), le « Nom de la réservation », éventuellement « Personnes ».",
          "« Définir le type de table » : la réservation est créée comme à la vente (bouteilles comprises du type ; ferme et QR si réglée). Elle apparaît au placement.",
          "Le type d'une réservation existante se change depuis Réservations → détail → « Changer le type de table » (motif).",
        ],
        warning:
          "Aucun montant ne bouge. Une table sur mesure manque aussi au placement tant que rien n'est payé : elle y entre au règlement, ou dès le premier acompte.",
      },
      {
        id: 'bracelets-commande',
        title: 'Changer les bracelets célibataire d\'une commande',
        who: "Administrateur, Responsable événement, Équipe (droit « Corriger le statut d'un bracelet »)",
        where: 'Commandes → ouvrir la commande → bloc « Bracelets célibataire : N »',
        steps: [
          "Une case par personne que la commande fait entrer : cochée = souhaite le bracelet. Badge « N bracelets célibataire » dans la liste des commandes et des réservations, colonne à l'export.",
          "Cocher ou décocher, motif facultatif → « Enregistrer ». La porte (au scan) et « Mon Masked Night » suivent aussitôt.",
        ],
        warning:
          "Une personne déjà entrée, ou dont le bracelet est déjà remis, se règle à la porte (case figée). L'équipe voit qui attend un bracelet, jamais la réponse donnée au formulaire.",
      },
      {
        id: 'annuler-rembourser',
        title: 'Annuler ou rembourser une commande',
        who: 'Administrateur, Responsable événement',
        where: 'Commandes → ouvrir la commande',
        steps: [
          "Si de l'argent a été encaissé : « Rembourser » → « Montant », « Motif ». Un remboursement total annule la vente.",
          "Puis « Retirer cette commande » → « Annuler la commande » + motif : le stock est libéré, les billets ne valent plus, le client reçoit l'e-mail « Votre commande … est annulée » avec ce motif.",
          "« Supprimer » : seulement une commande qui n'a jamais rien encaissé (doublon, essai).",
        ],
        warning:
          "Le système enregistre le remboursement, il ne rend pas l'argent : un remboursement PayPal se fait à la main dans PayPal. On annule toujours toute la commande, jamais une seule ligne.",
      },
      {
        id: 'supplements',
        title: 'Encaisser, annuler ou rembourser un supplément',
        who: 'Administrateur, Responsable événement, Équipe (encaisser) ; Administrateur, Responsable événement (annuler, rembourser)',
        where: 'Commandes → ouvrir la commande → cadre « Suppléments »',
        steps: [
          "Chaque lot de plats ajoutés : « Supplément n°… · montant », son état, sa référence (ESS…), ses lignes, « À régler : X » et l'échéance. La liste montre le total de la réservation et le badge « Supplément à régler ».",
          "Paiement reçu (vérifié dans PayPal avec la référence DU SUPPLÉMENT, pas celle de la table) : « Encaisser » → « Moyen », « Référence » → « Confirmer l'encaissement de X ». Seul le supplément devient réglé ; ses plats partent en cuisine le soir venu.",
          "« Renvoyer le règlement » renvoie au client l'e-mail du supplément (lignes, lien, référence), sans rien recréer.",
          "« Annuler le supplément » (motif) : seulement sans argent encaissé ; le stock revient, le client est prévenu, la réservation reste valable.",
          "« Rembourser » : borné à ce que le lot a encaissé. Rembourser la commande ne rembourse jamais un supplément, et inversement.",
        ],
        warning:
          "Une commande dont un supplément a été réglé ne s'annule pas tant que ce supplément n'est pas remboursé. Annuler une commande emporte ses suppléments non réglés.",
      },
      {
        id: 'fusionner-commande',
        title: 'Un client a deux numéros : fusionner dans la commande d\'entrée',
        who: 'Administrateur, Responsable événement',
        where: 'Commandes → ouvrir la commande de plats (« Liée à la commande d\'entrée »)',
        steps: [
          "Bloc « Fusionner dans la commande d'entrée » → « Fusionner dans la commande d'entrée ».",
          "Laisser « Prévenir le client par e-mail » coché → « Fusionner dans MN26-… ».",
          "Les plats deviennent un supplément de la commande d'entrée, dans l'état exact de l'ancienne commande (réglée ou non, encaissé, remboursé, référence, lien, échéance, caisse, vendeur). Le client reçoit « Un seul numéro pour votre réservation : MN26-… ».",
          "L'ancienne commande reste, close, « Fusionnée dans MN26-… (supplément n°…) » ; son lien de suivi mène à la réservation.",
        ],
        warning:
          "Les paiements et remboursements sont déplacés tels quels : chiffre d'affaires, remboursements, reste à encaisser, ventilations, caisses et « qui a vendu quoi » ne bougent pas d'un centime. Refusé pour une commande annulée, expirée ou remboursée, d'une autre édition ou devise, ou si l'entrée est close.",
      },
      {
        id: 'tables-personnalisees',
        title: 'Traiter une table personnalisée',
        who: 'Administrateur, Responsable événement',
        where: 'Demandes de table (carte « Personnalisée »)',
        needs: "Réglages → Tables → « Proposer « Personnaliser cette table » » ouvert (il l'est par défaut). Sur le site, chaque table a « Réserver » (inchangé) et « Personnaliser cette table ».",
        steps: [
          "La carte montre la « Table d'origine » (et sa formule) avec son prix affiché, puis chaque bouteille : « 2 → 1 modifiée », « 4 → 0 retirée » (barrée), « 0 → 1 ajoutée », la note du client, son e-mail et son WhatsApp. Le client a reçu « Votre demande est bien arrivée », sans prix.",
          "Au besoin, « Ajuster la composition » : nombre de personnes (dans la capacité de la table), quantités, « − » pour retirer, recherche pour ajouter une boisson de la carte, « Pourquoi ? » → « Enregistrer ». La demande du client reste affichée telle qu'il l'a faite (« Demande du client : … »).",
          "« Valider et fixer le prix » : « Prix (EUR) » au centime (pré-rempli avec le prix affiché), « Règlement » (« Lien PayPal + référence » ; « Espèces à l'entrée » ou « PayPal à l'entrée » seulement si ouverts dans Réglages → Vente), « Nom de la réservation », « Mot à la personne », la case de l'entrée comprise (cochée si la table affichée la comprend) → « Valider et envoyer le prix ».",
          "Aussitôt : une commande en attente de règlement au montant exact (une ligne « LOUNGE · personnalisée · 6 personnes »), une réservation avec LA composition validée et la formule d'origine, la table retenue jusqu'à l'échéance ; le client reçoit le prix, la composition, la référence et le lien PayPal (ou la consigne de régler à l'entrée) et la date limite. La carte affiche « Validée », la commande, la référence et le lien.",
          "Convives : « Enregistrer les prénoms » comme pour toute table.",
          "Paiement reçu : PayPal le constate seul, ou « Encaisser hors ligne » → « Marquer comme encaissée ». Les bouteilles validées sortent du stock. L'écran propose « Envoyer la confirmation maintenant ? ».",
          "« Envoyer la confirmation » : noms, QR, les bouteilles personnalisées. La réservation est dans Réservations (badge « Personnalisée », table d'origine), au placement, au bar et à l'écran du soir.",
          "Sinon : « Refuser » (motif lu par le client, qui reçoit « Au sujet de votre demande de table » ; la table reste réservable telle quelle) ou « Retirer ».",
        ],
        warning:
          "Aucun prix n'est calculé pour une table personnalisée : c'est le vôtre, au centime. Valider est refusé si la table est complète (« Cette table est complète ») : refusez alors la demande. Sans règlement à l'échéance, la tâche de nuit annule la commande et la table revient à la vente. Comme pour toute table, le règlement n'envoie pas la confirmation : cliquez « Envoyer la confirmation ».",
      },
      {
        id: 'demandes-table',
        title: "Une demande de l'ancien formulaire « sur mesure »",
        who: 'Administrateur, Responsable événement',
        where: 'Demandes de table (carte « Ancien formulaire « sur mesure » »)',
        needs: "Un type de table « Sur mesure » créé dans Boissons & tables (proposé d'office à l'acceptation). Le formulaire libre n'est plus sur le site : seules les demandes déjà reçues s'affichent.",
        steps: [
          "« Annoncer un prix » : « Prix (EUR) », « Mot à la personne », la case « L'entrée est comprise dans ce prix » si c'est le cas, « Envoyer le devis ».",
          "La personne accepte : « Elle a accepté » → « Type de table » (« Sur mesure » d'office) et « Nom de la réservation » → « Créer la commande et envoyer ». La commande (référence, lien PayPal, consignes par e-mail) et une vraie réservation de table sont créées : réglée, elle apparaît au placement de l'hôtesse.",
          "« Convives (N places) » : les prénoms donnés par le client, à compléter → « Enregistrer les prénoms ». Ils suivent la réservation : porte, placement, détail, confirmation.",
          "« Encaisser hors ligne » → « Marquer comme encaissée ». L'écran propose aussitôt « Envoyer la confirmation maintenant ? ».",
          "« Envoyer la confirmation » (actif une fois réglée) : remerciements, table, date, lieu, tous les noms et le QR de la table dans le message. Ensuite « Renvoyer la confirmation », avec la date et l'auteur du dernier envoi.",
          "Sinon : « Refuser » (motif, « Refuser et prévenir ») ou « Retirer ».",
          "Bandeau « Cette table a été acceptée avant la mise à jour : elle n'a pas encore de réservation (absente du placement). » : « Type de table » (« Sur mesure » d'office), « Nom de la réservation » → « Créer la réservation de table ». Type, nom, bouteilles comprises et convives sont ajoutés comme à une acceptation d'aujourd'hui ; réglée, la table passe au placement avec son QR. Aucun montant ne change. Puis « Envoyer la confirmation ». Même bandeau dans Commandes → la commande.",
        ],
        warning:
          "Le règlement n'envoie pas la confirmation d'une table sur mesure : sans clic sur « Envoyer la confirmation », le client n'a ni QR ni noms. Bandeau « Aucun billet d'entrée n'est configuré pour les tables » : l'entrée est comprise mais Réglages → Tables → « Type de billet pour l'entrée comprise » est vide — le QR part quand même, mais personne n'a de place nominative.",
      },
    ],
  },
  {
    id: 'argent-preparation',
    icon: '💶',
    phase: 'preparer',
    title: "Le budget, les objectifs, les stocks",
    intro:
      "Ce qu'on va dépenser, ce qu'on compte encaisser, ce qu'on a en réserve. Les trois se lisent ensemble.",
    procedures: [
      {
        id: 'budget',
        title: 'Poser le budget',
        who: 'Responsable finances, Responsable événement, Administrateur',
        where: 'Finances → Budget prévisionnel',
        steps: [
          "Les catégories de dépense se créent depuis Projet (« Amorcer l'espace projet »).",
          "« Définir le budget » : « Plafond total », « Pourquoi ce montant », « Ventilation par poste ».",
          "Une « Réserve non ventilée » est permise.",
        ],
        why: "Budget restant = prévisionnel − dépenses, sans regarder les recettes. Résultat = recettes encaissées − dépenses, sans regarder le budget.",
      },
      {
        id: 'objectifs',
        title: 'Fixer les objectifs de recettes',
        who: 'Responsable finances, Responsable événement',
        where: 'Objectifs',
        steps: [
          "« Modifier le cap » : l'objectif de recettes et les « Personnes attendues ».",
          "« Ajouter un objectif » : « Famille » (Entrées, Tables, Plats, Formules, Boissons, Extras), « Produit visé », « Libellé », « Quantité visée », « Prix unitaire retenu ».",
          "« Produit visé » : un produit précis ne compte que ses propres ventes ; « Toute la famille » compte toutes les ventes de la famille.",
        ],
        warning: "Deux objectifs sur la même cible sont refusés : une seule ligne « Toute la famille » par famille, une seule ligne par produit.",
      },
      {
        id: 'stock-saisie',
        title: "Renseigner les stocks et les prix d'achat",
        who: 'Responsable de domaine (livraisons), Responsable événement',
        where: 'Stocks · ou la fiche produit dans Boissons / Carte',
        steps: [
          "« Enregistrer un mouvement » → « Livraison », « Quantité », « Motif » (le responsable de domaine enregistre entrées et sorties).",
          "« Prix d'achat » et « Seuil de ce produit » : Responsable événement ou Administrateur. Le comptage physique exige « Corriger un stock » (inventory.movement.adjust).",
        ],
        warning: "Un produit sans stock saisi est illimité : jamais épuisé. Un prix d'achat vide ne vaut pas zéro : la ligne sort du total de la réserve, et l'écran le dit.",
      },
      {
        id: 'courses',
        title: 'Tenir la liste de courses',
        who: 'Responsables de domaine, Responsable finances',
        where: 'Courses',
        steps: [
          "« Quoi », « Qté », « Prix à l'unité » → « Ajouter ».",
          "Au retour, cocher la ligne : elle porte qui a acheté et quand.",
        ],
        why: "Cocher ne crée pas de dépense : enregistrez la dépense à part, dans Finances.",
      },
    ],
  },
  {
    id: 'repetition',
    icon: '🧪',
    phase: 'preparer',
    title: 'Répéter sur le test',
    intro:
      "Le soir J, chacun doit avoir déjà fait ses gestes une fois. Sur https://leyamo.test.tas-platform.com, rien ne coûte rien.",
    procedures: [
      {
        id: 'repetition-generale',
        title: 'La répétition générale (1 h 30)',
        who: "L'administrateur, et toute l'équipe du soir",
        where: 'https://leyamo.test.tas-platform.com',
        needs: 'Le système de test « Réinitialisé » juste avant (demande « Réussie » dans Système de test, sur la production), deux QR de table et le QR « Voter » imprimés',
        steps: [
          "Admin : ouvrir « Commande à table active » ; vérifier caisses, tables et serveurs, votes fermés avec nommés, jurés.",
          "Achat : un billet « En espèces à l'entrée », une table « En ligne par PayPal » (le lien ne mène nulle part : c'est normal) ; l'admin constate le paiement de la table.",
          "Chaque poste se connecte (et remplace son mot de passe provisoire) ; bar, cuisine et porte ouvrent chacun leur caisse avec un fond fictif.",
          "Porte : scanner le QR payé, taper un code de billet à la main, encaisser le non payé sur le scan (« Encaisser ici »), vendre 2 entrées, placer la table.",
          "Salle : commande par QR de table en espèces, encaissée par le serveur avec monnaie ; cuisine et bar « Terminé » ; une commande serveur en PayPal ; une annulation avec motif ; le bar marque une boisson épuisée puis la remet en vente.",
          "Le serveur remet ses espèces au bar : son « En poche » tombe à 0.",
          "Maître de cérémonie : « Programme » → modifier une heure, « Décaler la suite » de +10 puis −10, vérifier /programme.",
          "Concours : un tour lancé, votes ouverts, 3 téléphones votent, 2 jurés notent, gel ; lecture du tableau ; proclamer, publier, envoyer les résultats ; révéler un prix.",
          "Fermeture : essayer Clôture caisses ouvertes (refus attendu), puis chaque caisse « Clôturer », bilan du service, écran Clôture.",
          "Débrief, puis « Réinitialiser » le test.",
        ],
        why: "Le test n'envoie aucun vrai e-mail et ne prend aucun vrai paiement : on peut tout y essayer.",
      },
    ],
  },

  // ── Le jour J ────────────────────────────────────────────────────
  {
    id: 'avant-ouverture',
    icon: '⏱️',
    phase: 'jour-j',
    title: "L'ouverture",
    intro: "Une heure avant les portes : chaque poste se connecte et ouvre sa caisse.",
    procedures: [
      {
        id: 'checklist-ouverture',
        title: 'La liste de contrôle',
        who: 'Chacun à son poste',
        where: 'Partout',
        steps: [
          "Responsable : « Commande à table active » ouvert (Réglages) ; chaque table a son serveur (Service en salle) ; votes fermés avec leurs nommés (Votes).",
          "Chaque poste avec caisse (bar, cuisine, entrée, caissier) : /ops/pos → « Poste » → « Fond de caisse » compté → « Prendre le poste ». Chacun ouvre la caisse de son poste ; la liste ne propose que celles que le rôle peut tenir.",
          "Porte : se connecter sur /ops/checkin, faire un scan d'essai.",
          "Serveurs : /ops/service, vérifier leurs tables (« Prendre » → « 1. La table »).",
          "Cuisine et bar : onglet « Carte » → « Marquer épuisé » sur ce qui manque.",
          "Chacun ouvre son écran par le QR de son poste ou le lien reçu (QR & liens → « Écrans de l'équipe »). Sur téléphone, les écrans du serveur, de la cuisine et du bar rangent leurs quatre onglets sur deux rangées de deux, et le panier collé en bas (caisse, serveur) laisse voir les derniers produits : rien ne change dans les gestes.",
          "Maître de cérémonie : /ops/deroule, onglet « Programme » relu (heures, libellés, aucune alerte inattendue, sous-parties prêtes : pronostic, prestations « Tous les choix sont notés. », mieux habillé), onglet « Jury » (tous les jurés là), onglet « Écran » (la salle voit la bonne étape, « Aucun affichage du MC à l'écran »).",
          "Ludovic / responsable de soirée : écran de la salle allumé ; régie /ops/live ouverte sur son téléphone, cadre « Alertes » lu.",
          "Réserve : comptage d'ouverture.",
        ],
        warning: "Une personne ne tient qu'une caisse : « Vous tenez déjà une autre caisse. Clôturez-la d'abord. »",
      },
    ],
  },
  {
    id: 'regie',
    icon: '📡',
    phase: 'jour-j',
    title: 'La régie du soir',
    intro:
      "Celui qui répond de la soirée n'est à aucun poste : il les regarde tous, depuis /ops/live, sur téléphone comme sur ordinateur. Rien ne se saisit ici : on regarde, puis on va régler sur l'écran concerné.",
    notes: [
      {
        title: 'Qui voit quoi',
        body:
          "Administrateur et Responsable événement : tout. Responsable finances : les caisses, les espèces à remettre, les ventes, le stock et les commandes par serveur. Responsable projet, porte, serveurs, bar, cuisine, caisse et MC : « Votre rôle ne permet pas cet écran ». Chacun n'y voit que ce que son rôle lui permet de lire ; une section interdite n'apparaît pas (jamais de faux zéro).",
      },
      {
        title: 'Ce que montre la régie',
        body:
          "En tête : l'heure de la salle, le nombre d'alertes, « Actualisé à … », le bouton « Actualiser » et les raccourcis vers les écrans de l'équipe. Puis « Déroulé » (l'étape en scène et son retard) et « Alertes ». Dessous : « Entrées », « Cuisine et bar », « Commandes par serveur », « Caisses » (chacune), « Espèces à remettre », « Ventes » (encaissé net, sur place, par famille, par moyen de règlement, objectif de recettes), « Stock », « Tables » (dont « Groupes installés » et « Arrivés, à installer ») et le plan de salle, « Concours et votes » (lecture seule, aucun score).",
      },
    ],
    procedures: [
      {
        id: 'suivre-regie',
        title: 'Suivre la soirée et régler les alertes',
        who: 'Administrateur (Ludovic), Responsable événement ; Responsable finances pour sa partie',
        where: 'Régie du soir (/ops/live)',
        steps: [
          "Avant les portes : ouvrir /ops/live sur son téléphone et le laisser ouvert. Il se met à jour tout seul au rythme de « Rafraîchissement de l'écran » (15 s par défaut) ; en pause quand l'onglet est caché, il reprend en revenant ; sinon « Actualiser ».",
          "Regarder le nombre d'alertes en tête (vert : aucune ; rouge : une urgence).",
          "Cadre « Alertes » : chaque ligne est marquée « Urgent », « À surveiller » ou « Info » — commande qui attend trop en cuisine ou au bar, commandes de salle à encaisser, commandes en ligne en attente de paiement, caisse comptée avec un écart, espèces encore en poche chez un serveur, produit épuisé ou stock bas, réservation payée sans table, groupe arrivé et pas encore installé depuis plus de 10 minutes (« Urgent » à 20), demandes de table, retard du déroulé, scrutin hors délai encore ouvert.",
          "« Régler » sur l'alerte : le bon écran s'ouvre ; faire le geste (ou le demander au poste), revenir à la régie.",
          "Fin de nuit : « Espèces à remettre » dit « Tout a été remis. », chaque caisse est « Comptée », plus rien « À encaisser ».",
        ],
        warning:
          "« Connexion perdue — dernières données de … » en rouge : les chiffres affichés sont anciens. Attendre le réseau, puis « Actualiser ».",
        why: "Le seuil d'attente se règle dans Réglages → « Écran de salle » → « Alerte d'attente (minutes) » (15 par défaut).",
      },
    ],
  },
  {
    id: 'entree',
    icon: '🚪',
    phase: 'jour-j',
    title: 'La porte',
    intro:
      "Toute personne qui entre est enregistrée. En haut de l'écran : « Entrées » = les personnes contrôlées + les entrées vendues à la porte (« dont N vendues à la porte »), « Restants », « Attendus », « Bracelets ».",
    procedures: [
      {
        id: 'scan',
        title: "Quelqu'un a un billet",
        who: 'Équipe (porte)',
        where: 'Contrôle (/ops/checkin)',
        steps: [
          "« Scanner » → présenter le QR.",
          "QR illisible ou caméra refusée : dans le champ « Code du billet ou n° de commande », sous la caméra, taper le code imprimé sous le QR du billet, ou le numéro de commande → « Vérifier ». La liste du groupe s'ouvre comme après un scan : personne n'entre tout seul.",
          "Vert « Entrée validée » : faire entrer. Pour un groupe, nom par nom (« Faire entrer », « Personne suivante »).",
          "« Déjà entré » (heure et qui a scanné) : ne pas refaire entrer. « QR inconnu », « Autre édition » : appeler le responsable.",
          "Bracelet attendu : le donner → « Bracelet remis ».",
        ],
        warning: "Erreur de scan : « Annuler ce check-in » avec un motif d'au moins 5 caractères ; la personne peut ensuite entrer normalement.",
      },
      {
        id: 'non-regle',
        title: 'Encaisser une commande « Non réglée »',
        who: "Le membre de la porte qui tient la caisse de l'entrée (ou une caisse « tout le catalogue »)",
        where: 'Contrôle → résultat du scan → « Encaisser ici »',
        needs: "La caisse de l'entrée ouverte (/ops/pos)",
        steps: [
          "Le scan affiche « Non réglé » et « reste à encaisser : X » ; une réservation réglée à l'entrée affiche en grand « Montant à régler — X € ». Les lignes du groupe sont verrouillées (« À régler d'abord ») et la table ne reçoit pas de numéro avant l'encaissement : payer, puis entrer.",
          "« Espèces » : taper ce qu'on reçoit dans « Montant reçu » (« Compte juste » s'il fait l'appoint) → « Espèces reçues ».",
          "« Carte » : passer la carte sur le terminal de l'entrée → « Carte acceptée ». « PayPal » : vérifier sur le téléphone du client, avec la référence → « PayPal reçu ». Carte et PayPal sont rattachés à la caisse sans entrer dans le tiroir.",
          "« Encaissé — inscrit dans la caisse … Le groupe peut entrer. » et « À rendre » en grand : rendre la monnaie, puis faire entrer nom par nom, ou placer la table.",
          "La réservation dit ensuite « Réglée à l'entrée — moyen — le … par … » (Commandes et Réservations) ; le geste est au journal avec la monnaie rendue et la caisse.",
        ],
        warning:
          "Sans caisse : « Pour encaisser ici, ouvrez d'abord la caisse de l'entrée. » (lien « Ouvrir la caisse »). Avec la caisse d'un autre poste : refusé, l'argent de la porte va dans la caisse de l'entrée. Sans le droit : « faites venir la personne qui tient la caisse de l'entrée ».",
      },
      {
        id: 'installer-tables',
        title: 'Placer une table',
        who: 'Équipe (porte)',
        where: 'Contrôle → résultat du scan de la table',
        needs: 'Le plan de salle avec les numéros',
        steps: [
          "Le résultat affiche « À placer — N personnes ».",
          "Choisir la table : les tables libres du bon type d'abord, puis « Autres tables » → « Table attribuée ».",
          "« Changer de table » ou « Libérer la table » en cas d'erreur.",
          "Table déjà attribuée d'avance : son numéro s'affiche en grand — l'annoncer au groupe.",
        ],
        warning: "Groupe déjà installé par l'hôtesse : « Installés à … par … — table bloquée ». La porte ne change plus sa table ; c'est l'hôtesse qui le fait, avec un motif.",
        why: "Le numéro attribué est celui que voient les serveurs, la cuisine et le bar — et l'hôtesse, qui voit le groupe arriver au même instant.",
      },
      {
        id: 'hotesse-installer',
        title: 'Accueillir et installer un groupe',
        who: "Hôtesse d'accueil (ou toute personne avec tables.placement.update)",
        where: 'Salle (/ops/service) → Placement',
        steps: [
          "Ouvrir /ops/service et se connecter avec son compte. Une hôtesse seule voit directement le placement ; avec aussi le rôle Service, toucher « Placement » en haut.",
          "« Arrivés — à installer » est en tête, encadré : le groupe vient de passer la porte (« 1 / 4 arrivés », puis « Tous arrivés »). Le numéro de table est en grand, à gauche ; l'heure d'arrivée et l'attente sont dessous. L'écran se met à jour tout seul toutes les dix secondes.",
          "Accompagner le groupe à sa table.",
          "Une fois assis : « Installer » → « Oui, installés ». La carte passe dans « Installés », avec l'heure et votre nom. La table est bloquée.",
          "Pas de table encore ? « Attribuer une table » d'abord, puis « Installer ».",
          "Erreur de table : « Changer de table ». Si le groupe est déjà assis, écrire le motif, puis toucher la nouvelle table : l'ancienne se libère, la nouvelle se bloque.",
          "Erreur d'installation (mauvais groupe, mauvais bouton) : « Annuler l'installation », écrire le motif → « Annuler l'installation ». La table reste attribuée mais n'est plus bloquée.",
        ],
        warning: "Un groupe arrivé depuis plus de 10 minutes sans être installé déclenche une alerte dans la régie (/ops/live).",
        why: "Chaque geste — attribuer, changer, installer, annuler — est écrit au journal avec votre nom : on sait le lendemain qui a installé quelle table.",
      },
      {
        id: 'compteur-porte',
        title: "Quelqu'un arrive sans billet",
        who: 'Équipe (porte)',
        where: 'Contrôle → Entrées payées sur place',
        steps: [
          "Toucher la formule (SOLO, COUPLE…), « Moins » / « Plus » pour le nombre.",
          "« Liquide » ou « PayPal ».",
          "Le résumé dit « N personnes pour X € » → « Enregistrer l'entrée ».",
        ],
        why: "Les espèces entrent dans la caisse de l'entrée (ou « tout le catalogue ») tenue par la personne qui enregistre ; avec la caisse d'un autre poste, c'est refusé ; sans caisse, l'entrée est comptée à son nom (« en poche »). Seule la personne qui a saisi l'entrée, ou un responsable qui peut fermer la caisse d'un autre (pos.session.close_any), peut la « Retirer ». Ces entrées comptent dans « Entrées » et dans le quota de la vente en ligne.",
      },
    ],
  },
  {
    id: 'service-salle',
    icon: '🤵',
    phase: 'jour-j',
    title: 'Les serveurs et les tables',
    intro:
      "Chaque table a son serveur. Toute commande a un numéro, une table, un serveur. Rien ne part en cuisine ou au bar avant d'être réglé.",
    notes: [
      {
        title: "Où va l'argent d'un serveur",
        body:
          "Un serveur ne tient pas de caisse. Ses espèces sont comptées à son nom : son « En poche », en haut de l'écran. Il le remet au caissier d'un poste (bar, cuisine, entrée), qui compte et enregistre. À la fin, « En poche » doit être à zéro pour tout le monde. PayPal n'entre dans aucune poche.",
      },
    ],
    procedures: [
      {
        id: 'prendre-commande',
        title: 'Prendre une commande',
        who: 'Serveur',
        where: 'Service (/ops/service) → Prendre',
        steps: [
          "« 1. La table » : vos tables ; « Toutes les tables » pour aider un collègue (la commande sera à votre nom).",
          "Un client sans table (debout au bar, près de la piste) : la première case, « Sans table » → « Prénom du client » (obligatoire), « Repère (facultatif) », puis « Qui la remet ? » : « Je l'apporte » ou « À retirer au comptoir » (le poste appelle le numéro et le prénom).",
          "« 2. Les articles » : une touche par unité. « Épuisé » est impossible.",
          "« Un mot pour la cuisine ou le bar » si besoin ; « Espèces » ou « PayPal » ; « Envoyer la commande ».",
          "La commande est dans « À encaisser » — avec ou sans table, elle est à vous : vous l'encaissez, un collègue ne peut ni l'encaisser en poche ni l'annuler.",
        ],
      },
      {
        id: 'commande-table',
        title: "L'invité commande depuis sa table",
        who: "L'invité, seul",
        where: 'QR de la table → /commander?table=…',
        steps: [
          "« Votre table » : numéro, nom, « Votre serveur » → « C'est ma table ».",
          "Il compose son panier ; le total est « calculé par nos serveurs ».",
          "PayPal ou « Espèces » (« Combien avez-vous sur vous ? » facultatif) → « Commander ».",
          "La commande arrive dans « À encaisser » chez le serveur de la table (chez tous si la table n'a pas de serveur).",
          "Sa page se tient à jour seule : « En attente du paiement », « En préparation », puis « Prête ». Elle reste affichée si l'on recharge ; « Passer une autre commande » repart d'un panier vide.",
        ],
        warning: "Seulement si « Commande à table active » est ouvert ; sinon « La commande à table n'est pas ouverte pour le moment. »",
      },
      {
        id: 'commande-sans-table',
        title: "L'invité commande sans table",
        who: "L'invité, seul",
        where: '/commander (QR « Commander (sans table) ») → « Je n\'ai pas de table »',
        steps: [
          "« Votre prénom » (obligatoire), « Où êtes-vous ? (facultatif) » → « Continuer ».",
          "Il compose son panier, choisit PayPal (référence à recopier) ou « Espèces » — qu'il règle au comptoir en donnant son numéro.",
          "Écran final : « Votre commande n° … — Retirez-la au comptoir quand elle est prête. », puis « Prête — venez la chercher au comptoir » dès que le poste touche « Terminé ».",
          "Côté équipe : aucun serveur ne la voit. Le poste l'affiche « Sans table · prénom — à retirer au comptoir », l'encaisse dans sa caisse, la prépare, puis appelle le numéro et le prénom.",
        ],
        warning:
          "Seulement si « Commande à table active » et « Commande sans table autorisée » sont ouverts ; fermé, le bouton disparaît et l'API refuse (« La commande sans table n'est pas ouverte ce soir : indiquez votre table. »). Personne d'autre qu'une caisse ou le responsable de salle ne l'encaisse.",
      },
      {
        id: 'encaisser-table',
        title: 'Encaisser à table',
        who: 'Serveur',
        where: 'Service → À encaisser → Encaisser',
        steps: [
          "Le moyen annoncé est présélectionné ; le changer si l'invité paie autrement.",
          "Espèces : « Montant reçu » (« Compte juste » s'il fait l'appoint) → « Espèces reçues » ; rendre « À rendre ».",
          "PayPal : vérifier sur le téléphone de l'invité, avec la référence → « PayPal vérifié ».",
          "Le message dit où est l'argent : « Inscrit dans la caisse… » ou « Compté à votre nom… » → « Compris ».",
          "Suivre dans « En cours » : « Cuisine · Terminé », « Bar · … ».",
        ],
        warning:
          "Moins que le dû est refusé. Un serveur n'encaisse que les commandes de ses tables, des tables sans serveur, ou celles qu'il a prises, avec ou sans table (« Cette commande est celle d'un autre serveur, ou du comptoir… ») — sauf s'il tient une caisse, ou s'il est responsable de salle (service.table.assign). La commande d'un invité sans table n'est à aucun serveur : une caisse ou le responsable de salle l'encaisse.",
      },
      {
        id: 'annuler-commande',
        title: 'Annuler une commande',
        who: 'Serveur (ses commandes), Bar, Cuisine',
        where: 'Service → la carte → Annuler · /ops/cuisine · /ops/bar',
        needs: 'Un motif, toujours',
        steps: [
          "Non réglée : le stock est libéré, aucun argent ne bouge.",
          "Réglée par vous : remboursée en entier, rendez l'argent ; votre « En poche » baisse d'autant.",
          "Ce qui était en préparation est compté en perte, le reste revient en stock.",
        ],
        warning:
          "Réglée par quelqu'un d'autre : « l'annulation se fait au poste qui l'a encaissée ». L'annulation porte sur toute la commande : un plat manque, on annule et on reprend sans lui.",
      },
      {
        id: 'remise-especes',
        title: 'Remettre ses espèces à une caisse',
        who: 'Serveur, et celui qui tient la caisse d\'un poste (Bar, Cuisine, Équipe de la porte, Caisse)',
        where: 'Caisse (/ops/pos) → Mon tiroir → Remises des serveurs',
        steps: [
          "Sur la caisse, un badge prévient : « N personnes ont des espèces à remettre (montant) » ; le toucher ouvre le tiroir.",
          "Le serveur apporte ses billets à celui qui tient la caisse d'un poste.",
          "Le caissier voit le serveur et son montant, compte, corrige « Montant compté » si besoin → « Reçu ».",
          "L'« En poche » du serveur baisse d'autant.",
        ],
        warning: "Plus que ce que le serveur doit est refusé. Le serveur ne déclare jamais lui-même une remise.",
      },
    ],
  },
  {
    id: 'postes-caisses',
    icon: '🍳',
    phase: 'jour-j',
    title: 'Cuisine, bar et caisses',
    intro:
      "Les postes préparent ce qui est réglé, encaissent ce qu'on vient leur payer, et tiennent leur caisse jusqu'à la fermeture.",
    procedures: [
      {
        id: 'postes',
        title: 'Préparer et servir',
        who: 'Bar, Cuisine',
        where: '/ops/bar · /ops/cuisine',
        steps: [
          "« En cours » : les commandes réglées, la plus ancienne en tête — numéro, table, serveur, et seulement les lignes de votre poste.",
          "Sans table, la carte dit à qui la remettre : « Sans table · Paul » + « (près de la piste) · servi par Marc » (le serveur l'apporte) ou « à retirer au comptoir » (appelez le numéro et le prénom) ; « Comptoir · Paul » pour une vente de caisse avec un prénom.",
          "Le chrono part du règlement : orange à 10 minutes, rouge à 20.",
          "Prêt et sorti : « Terminé ». Le serveur le voit aussitôt.",
          "Une commande non réglée apparaît avec « Encaisser » : on ne la prépare jamais avant.",
          "Un client vient régler sa table au poste : « Encaisser » (espèces avec « Montant reçu », ou PayPal vérifié). Il faut tenir la caisse du poste — lien « Ma caisse » en haut de l'écran ; sinon « Ouvrez la caisse de ce poste pour encaisser ou rembourser ici. »",
        ],
      },
      {
        id: 'epuise',
        title: 'Marquer un article épuisé',
        who: 'Bar, Cuisine (catalog.stock.update + bar.order.read ou kitchen.order.read)',
        where: '/ops/bar · /ops/cuisine → onglet « Carte »',
        steps: [
          "« Marquer épuisé » sur le produit : il apparaît barré, « épuisé depuis … ».",
          "Il disparaît partout à la fois : commande à table, écran des serveurs, caisse, pré-commande en ligne (« Épuisé » sur /carte).",
          "« Remettre en vente » l'annule.",
        ],
        warning: "Le stock n'est pas modifié : c'est la vente qui s'arrête. Sans le droit : « Votre rôle ne permet pas de marquer un produit épuisé. »",
      },
      {
        id: 'caisse',
        title: 'Vendre au comptoir',
        who: 'Celui qui tient la caisse du bar, de la cuisine, ou le rôle Caisse (la porte ne vend rien au comptoir)',
        where: 'Caisse (/ops/pos)',
        steps: [
          "Toucher les produits → « Espèces », « Carte » ou « PayPal ».",
          "En espèces, « Reçu » (vide = compte juste) → « Encaisser » → rendre « À rendre » → « Client suivant ».",
          "« Prénom (pour l'appeler) », facultatif : pour un plat qui ne part pas tout de suite. Le poste lit « Comptoir · Paul » ; le ticket montre le numéro et « appelez « Paul » » ; « Mes ventes » le garde.",
          "Une erreur : « Mes ventes » → « Annuler cette vente » → motif obligatoire → « Annuler pour de bon ». Le stock revient, le tiroir est corrigé.",
          "« Mon tiroir » : « Apport », « Sortie », « Correction » + « Montant » + « Motif » → « Inscrire ».",
        ],
        warning:
          "« Mes ventes » n'annule que vos ventes du service en cours ; après la clôture ou pour la vente d'un collègue, « l'annulation passe par l'administration ». Une commande de salle (à une table ou sans table) s'annule depuis l'écran du poste. Une caisse Bar ne vend que ce que le bar sert, une caisse Cuisine que les plats et packs.",
      },
      {
        id: 'fermer-caisses',
        title: 'Fermer sa caisse',
        who: 'Chaque caissier de poste',
        where: 'Caisse → Clôturer',
        steps: [
          "D'abord, recevoir les dernières remises des serveurs.",
          "« Clôturer » → compter → « Montant compté dans le tiroir » (le théorique est caché exprès).",
          "S'il y a un « Écart » : « Motif de l'écart (obligatoire) » → « Clôturer la caisse ».",
          "Lire le « Rapport de fermeture », remettre le tiroir au trésorier.",
        ],
        warning:
          "Un caissier est parti sans fermer : Finances → Les services de caisse → « Fermer cette caisse à la place de son titulaire » (Responsable finances, Responsable événement, Administrateur), avec le compté et un motif. Une caisse encore ouverte bloque la clôture de l'édition.",
      },
    ],
  },
  {
    id: 'ceremonie',
    icon: '🎤',
    phase: 'jour-j',
    title: 'La cérémonie et les votes',
    intro:
      "Le maître de cérémonie conduit tout depuis son conducteur (/ops/deroule) : déroulé, votes, tableau privé, proclamation, prix, jury. Un responsable du vote peut l'aider sur une tablette, c'est facultatif. Les jurés notent ; la salle vote. Rien n'est public avant la publication des scores.",
    notes: [
      {
        title: 'Le conducteur en un coup d\'œil',
        body:
          "En haut, sur chaque onglet : « Maintenant » (l'étape en cours, « 12 / 20 min » écoulées sur prévues, en orange si elle dépasse), « Ensuite » (heure et nom de la suivante) et un badge « +N min » (retard), « À l'heure » ou « N min d'avance » (moins de 3 min d'écart = à l'heure). Six onglets : « Direct », « Programme », « Votes », « Concours », « Jury », « Écran » — en bas de l'écran sur téléphone, sous l'en-tête sur ordinateur. L'onglet ouvert reste dans l'adresse (?onglet=…) : un rechargement rouvre le même. Un onglet ou un bouton dont le compte n'a pas le droit n'apparaît pas.",
      },
    ],
    procedures: [
      {
        id: 'derouler',
        title: 'Conduire la soirée et un tour',
        who: 'Maître de cérémonie',
        where: 'Conducteur (/ops/deroule) → onglet « Direct »',
        steps: [
          "En tête : « Terminer l'étape en cours » et « Lancer : … » (la suivante). Sur chaque étape de la liste : « Démarrer » / « Sauter », « Terminer », « Réinitialiser ». « Démarrer » termine la précédente ; l'écran de la salle suit. Pas de confirmation.",
          "Retard de 3 min ou plus : cadre orange « La soirée a N min de retard sur le déroulé. » → « Décaler la suite de N min » (arrondi aux 5 min du dessus) → ajuster → « Décaler N étapes de X min ». Seules les étapes à venir bougent.",
          "Étape d'un tour : les tours Miss et Master rattachés démarrent ; tout se pilote DANS la carte de l'étape, sous « Sur scène — concours et votes » : « Terminer le tour » / « Reprendre le tour », un bouton par candidat du tour (« #1 Aïcha »…), le vote du tour et un départage ouvert ; puis « Feuille de l'étape en cours ». « Aller à l'étape en cours : … » en haut y fait défiler. Un tour lancé hors de l'étape en cours (depuis « Concours ») apparaît en tête, cadre orange « Sur scène, hors de l'étape en cours ».",
          "Le défilé : toucher le nom de qui entre sur le podium → « À l'écran » ; l'écran de la salle montre son profil en grand à côté du programme (moins de 5 s). « Suivant : #3 » passe au dossard suivant ; « Personne à l'écran » (ou toucher de nouveau le nom) rend l'écran au programme. « Terminer le tour » efface le profil.",
          "Annoncer, choisir la « Durée » (« 2 min »…, ou « Sans durée »), puis « Ouvrir le vote — 2:00 » sur chaque vote du tour. Le décompte s'affiche au conducteur, sur l'écran de la salle (avec les nommés en photo, sans bouton) et sur la page de vote ; le compteur « N bulletins » monte.",
          "Onglet « Jury » → « Qui a noté quoi » : tous en vert ? Sinon relancer de vive voix le juré en orange. Puis « Geler ».",
        ],
        warning: "Démarrer ou terminer une étape n'ouvre ni ne gèle le vote : c'est toujours un geste du maître de cérémonie. Un bouton que le compte n'a pas le droit d'utiliser n'apparaît pas (« Votre compte ne peut pas ouvrir ni fermer ce vote. »).",
        why: "Le maître de cérémonie mène la soirée d'ici, sans changer d'écran. Il n'ouvre pas la régie /ops/live.",
      },
      {
        id: 'duree-vote',
        title: "La durée d'un vote (le décompte)",
        who: 'Maître de cérémonie (voting.category.control — Responsable événement, Administrateur aussi)',
        where: 'Conducteur (/ops/deroule) → « Direct », « Votes » ou « Concours » ; aussi Votes → la catégorie → « Conduite du scrutin »',
        steps: [
          "Avant d'ouvrir : « Durée » → « Sans durée », « 1 min », « 2 min », « 3 min », « 5 min », ou minutes et secondes à la main (1 s à 60 min). Le bouton devient « Ouvrir le vote — 2:00 ». La durée du tour précédent est proposée d'office.",
          "Pendant le vote : « Temps restant » en grand (« sur 2:00 »), puis « +30 s », « +1 min », « Relancer » (choisir une durée → « Relancer »), « Arrêter le décompte » (le vote reste ouvert ; « Lancer le décompte » en remet un).",
          "L'écran de la salle montre le même décompte en très grands chiffres (orange et pulsant les dix dernières secondes), puis « 0:00 — Temps écoulé ». La page de vote affiche « Temps restant 1:29 », puis « Le vote se termine bientôt ».",
          "À zéro : « Temps écoulé — fermez le vote quand vous voulez. », « Geler » passe en orange. Geler ou fermer quand la salle a fini ; « +30 s » après zéro redonne trente secondes à partir de maintenant.",
        ],
        warning:
          "Le temps est écoulé mais le vote est encore ouvert ? C'est voulu : le décompte est une annonce, il ne ferme RIEN — les convives peuvent encore voter jusqu'à « Geler » ou « Fermer ». Seule l'heure « Fermeture automatique » réglée dans Votes refuse les bulletins après l'heure.",
        why: "Une ovation, un candidat qui revient saluer : la salle n'a pas toujours fini à la seconde annoncée. Le décompte presse les indécis, la décision de fermer reste au maître de cérémonie. Chaque geste (ouvrir avec une durée, +30 s, relancer, arrêter) est inscrit au journal.",
      },
      {
        id: 'conducteur-votes',
        title: 'Votes, concours, jury et écran, depuis le conducteur',
        who: 'Maître de cérémonie (Responsable événement, Administrateur aussi)',
        where: 'Conducteur (/ops/deroule) → onglets « Votes », « Concours », « Jury », « Écran »',
        steps: [
          "« Votes » : « Le vote de la salle » — « Page de vote (/vote) » et « Page des résultats (/resultats) », chacune avec « Afficher le QR » et « Copier le lien ». Puis « Votes libres » et un bloc par concours : « Durée » et « Ouvrir le vote », le décompte (« +30 s », « +1 min », « Relancer », « Arrêter le décompte »), « Geler », « Fermer » ; pour un vote libre « Publier » → « Confirmer », et « Annuler la publication » → motif → « Retirer la publication ». Sous chaque vote : « N votants » et « Voir le dépouillement » (replié par défaut).",
          "« Concours » : choisir le concours ; « Tours — … » (« Démarrer le tour » / « Terminer le tour » à la main, « Sur scène », ses votes) ; « Classement privé » — le même tableau que dans Votes (public 70 % + jury 30 %, départage, proclamer, publier les scores, envoyer les résultats, annuler avec motif) ; « Prix et podiums » → « Ouvrir le podium » → saisir les rangs → « Enregistrer le podium » → « Révéler le podium » (ou « Masquer à nouveau »).",
          "« Jury » : choisir le concours ; « Qui a noté quoi » (par tour « N/M notes », par juré ses candidats notés ; aucune note montrée, relu toutes les 15 s) ; « Les jurés » : « Ajouter un juré », « Afficher le QR », « Envoyer le lien », « Nouveau lien », « Révoquer », « Retirer » (juré sans note).",
          "« Écran » : « À l'écran en ce moment » (ce que la salle voit : maintenant, ensuite, plus tard, Cake Moment, vote ouvert et son décompte, départage, vainqueur). Un affichage posé par le MC (pronostic, gagnant, question du quiz) apparaît en tête dans un cadre vert, avec « Retirer l'affichage ». « Liens à afficher » : le programme public avec son QR.",
        ],
        warning:
          "Les scores des tours ne se publient pas dans « Votes » mais avec le concours, dans « Concours ». L'adresse de l'écran de la salle (avec sa clé) ne s'affiche que pour un compte qui peut modifier les réglages (Administrateur, Responsable événement) ; le MC lit « l'administration la remet (QR & liens) ».",
        why: "Le MC ne crée rien ici : concours, tours, votes, nommés et prix se préparent dans le back-office. Il conduit ce qui existe, et peut gérer les jurés le soir même (competition.jury.manage).",
      },
      {
        id: 'preparer-deroule',
        title: 'Préparer et rattraper le déroulé',
        who: "Maître de cérémonie (ou responsable d'événement)",
        where: 'Conducteur (/ops/deroule) → onglet « Programme »',
        steps: [
          "Onglet « Programme » du conducteur (les onglets sont en bas de l'écran sur téléphone). En tête : le Cake Moment (l'activer, choisir l'étape qui le déclenche), puis la « Fin prévue », le nombre d'étapes et d'alertes.",
          "« Modifier » sur une étape : « Début » (heure de l'édition), « Durée (min) », « Libellé » dans chaque langue (la langue par défaut est obligatoire), « Animation » (ou « Aucune animation »), « Animée par », « Lieu », « Notes du MC (jamais affichées au public) », « Visible du public et sur l'écran de la salle ». « Enregistrer ».",
          "« Ajouter une étape » en bas, ou « Insérer après » sous une étape : l'heure proposée est la fin de l'étape précédente.",
          "↑ / ↓ pour changer l'ordre. « Retirer » → « Oui, retirer » : l'étape disparaît aussi du programme public et de l'écran. L'étape en cours ne se retire pas (pas de bouton) : la terminer ou la sauter d'abord.",
          "Retard en soirée : « Décaler la suite » (en haut, à partir de la prochaine étape prévue) ou « Décaler à partir d'ici » (sous une étape), choisir −15, −5, +5, +10, +15, +30 ou taper un nombre dans « Minutes », puis « Décaler N étapes de X min ». Seules les étapes à venir bougent ; ce qui est en cours ou terminé ne bouge pas.",
          "Sous chaque étape, des alertes qui ne bloquent rien : « Chevauche « … » de N min », « Trou de N min avant cette étape », « Commence avant « … », placée au-dessus » (l'heure ne suit pas l'ordre de la liste), « Sans heure ».",
        ],
        warning: "Tout ou rien : si une seule heure décalée sortait de la nuit de l'événement (plus de 12 h avant ou après son début), rien n'est décalé. C'est le même programme que le site et l'écran : /programme suit en une minute au plus, l'écran de la salle à son rythme (Réglages → « Écran de salle » → « Rafraîchissement de l'écran »). Une étape liée au concours ou au gâteau garde son lien (il se règle dans l'administration). Chaque geste est inscrit au journal d'audit.",
        why: "Le MC tient sa feuille de route lui-même : plus besoin de faire passer une heure ou un libellé par l'administration, ni de recopier vingt horaires quand la soirée prend du retard.",
      },
      {
        id: 'feuille-mc',
        title: "La feuille du MC : sous-parties, pronostic, prestations, quiz, gagnants à l'écran",
        who: "Maître de cérémonie (Responsable événement, Administrateur aussi) — afficher à l'écran : program.screen.control",
        where: 'Conducteur (/ops/deroule) → onglet « Programme » → « Sous-parties (N) » ; le soir : onglet « Direct »',
        steps: [
          "Préparer : sous une étape, « Sous-parties (N) » → « Ajouter une sous-partie » → « Note » (titre et texte, long permis), « Pronostic » (consignes facultatives), « Désignation d'un gagnant » (par exemple « Meilleur habillement du public ») ou « Prestations des candidats » (choisir le concours) → « Enregistrer ». ↑ / ↓, « Modifier », « Retirer ».",
          "Prestations, avant la soirée : pour chaque candidat retenu (numéro, photo, nom public), « Prestation » → « Chanter », « Danser » ou « Parler d'un sujet » (le « Sujet » est alors obligatoire), « Notes (facultatif) » : chanson, musique… → « Enregistrer ». En tête : « N choix manquants » ou « Tous les choix sont notés. ».",
          "Quiz du public : « Ajouter une sous-partie » → « Quiz du public » → « Enregistrer », puis autant de questions que voulu : « Ajouter une question » → « Question », « Réponse A » à « Réponse D » (toutes obligatoires), « Bonne réponse (facultatif) » (Aucune, A, B, C, D), « Note pour le MC » → « Enregistrer ». ↑ / ↓, « Modifier », « Retirer ».",
          "Le soir, onglet « Direct » : dans la carte de l'étape en cours (ou de la suivante si rien ne tourne), « Feuille de l'étape en cours » montre les sous-parties en grand ; les autres étapes ont « Sous-parties (N) », qui se déplient dans leur carte.",
          "Quiz, le soir : « Afficher la question 1 », puis « Question suivante (2/10) » ; sur chaque question « Afficher à l'écran ». La question à l'écran (cadre vert) a « Révéler la réponse » (si une bonne réponse est notée), « Reposer (réponse cachée) » et « Retirer de l'écran ». L'écran montre « Question 3 / 10 », la question en très grand et quatre cases A à D en 2 × 2 ; à la révélation la bonne case s'allume en or avec ✓, les autres s'éteignent.",
          "Pronostic : choisir Miss, Master ou les deux → « Afficher les candidats à l'écran » (le profil complet de chaque candidat retenu, par numéro : photo, nom de scène, âge, activité, études, trois mots, adage et présentation ; un profil incomplet s'affiche avec ce qu'il a) → « Retirer de l'écran » quand la salle a noté. Après dépouillement : « Gagnant (table ou personne) », « Précision » → « Publier le gagnant à l'écran ».",
          "Mieux habillé (ou toute désignation) : noter le gagnant → « Publier le gagnant à l'écran » → « Retirer de l'écran ». Le cadre vert « À l'écran en ce moment » (Direct et Écran) rappelle ce qui est affiché, avec « Retirer l'affichage ».",
        ],
        warning: "Un seul affichage à la fois : publier autre chose remplace ce qui était à l'écran. Corriger un nom après publication ne change pas l'écran : « Republier le gagnant ». Un gagnant noté n'est jamais publié sans « Publier le gagnant à l'écran ».",
        why: "Tout ce que le MC menait sur papier est sous l'étape où ça se joue. C'est privé : ni le programme public ni l'écran ne montrent une sous-partie, sauf ce que le MC y affiche. Chaque affichage et chaque retrait est inscrit au journal (qui, quand).",
      },
      {
        id: 'jures',
        title: 'Noter, pour un juré',
        who: 'Chaque juré, sans compte',
        where: 'Son lien personnel /jury',
        steps: [
          "Sous le tour marqué « Notation ouverte », une note de 1 à 10 par candidat : enregistrée aussitôt.",
          "La note se corrige tant que le vote du tour est ouvert ; « Figé » ensuite.",
          "Le juré ne voit que ses notes.",
        ],
        warning: "Lien perdu : le MC, conducteur → onglet « Jury » (ou Miss & Master → Le jury) → « Nouveau lien » → « Afficher le QR ».",
      },
      {
        id: 'vote-public',
        title: 'Le vote de la salle',
        who: 'Le public',
        where: '/vote',
        steps: [
          "Par le QR « Voter » des tables — seulement si le vote a « Réservé aux présents » décoché.",
          "Ou par le lien « Voter maintenant » de « Mon billet », qui apparaît après le scan à l'entrée.",
          "« Je vote » sur un candidat ; une voix par personne et par vote.",
        ],
        warning:
          "Le lien du billet et le QR de table ouverts dans le même navigateur partagent un seul quota. Mais rien n'empêche un second téléphone ou une fenêtre privée : seul « Réservé aux présents » ferme complètement cette porte.",
      },
      {
        id: 'tableau-prive',
        title: 'Lire le tableau privé',
        who: 'Responsable du vote : Maître de cérémonie, Responsable événement ou Administrateur',
        where: 'Conducteur → onglet « Concours » → « Classement privé », ou Votes → Miss & Master — tableau privé (le même tableau)',
        steps: [
          "Un onglet par tour : « notes du jury : 11 / 12 » (relancer le juré avant le gel), la note sur 100 de chacun, les voix, chaque note de juré.",
          "Note du tour = ses voix ÷ toutes les voix × 70 + ses points de jury ÷ tous les points × 30. Exemple : 60 voix sur 100 et 16 points sur 40 → 42 + 12 = 54.",
          "« Cumul » : la somme des trois tours, sur 300.",
          "Égalité en tête : le total des points du jury départage (« départagé par le jury »).",
        ],
        warning: "Une note de juré manquante compte pour 0. Personne n'a voté dans un tour : la part public vaut 0 pour tous.",
      },
      {
        id: 'departage',
        title: "Départager une égalité que le jury n'a pas tranchée",
        who: 'Responsable du vote (ou le Maître de cérémonie seul)',
        where: 'Conducteur → onglet « Concours » → « Classement privé », ou Votes → Miss & Master — tableau privé',
        steps: [
          "« Égalité au cumul et aux points du jury : départage nécessaire. » → « Ouvrir le départage à 5 voix ».",
          "Désigner cinq personnes de la salle ; chacune scanne un des cinq QR et vote une fois.",
          "Suivre « x / 5 jurés ont voté » et « En tête du départage » (visible de vous seul).",
          "« Geler » le départage (au conducteur ou dans Votes).",
          "Page rechargée ou QR masqués : « Réafficher les QR du départage » — les mêmes QR, toujours valables, aucune voix perdue ; ceux qui ont servi sont grisés.",
          "Départage gelé encore à égalité : « Relancer un départage » — « Départage n° 2 », cinq nouveaux QR ; le précédent reste enregistré avec ses voix.",
        ],
        warning:
          "« Retirer et redonner les 5 bulletins » tire de nouveaux QR et efface les voix déjà données : seulement si un bulletin est perdu. Publier les scores publie aussi les départages.",
      },
      {
        id: 'proclamer-publier',
        title: 'Proclamer, publier, envoyer les résultats',
        who: 'Responsable du vote (competition.result.reveal : Maître de cérémonie, Responsable événement, Administrateur)',
        where: 'Conducteur → onglet « Concours » → « Classement privé », ou Votes → Miss & Master — tableau privé',
        steps: [
          "Tous les votes du concours (et le départage) sont gelés.",
          "« Proclamer le vainqueur » → « Oui, proclamer » : la salle, l'écran et /resultats apprennent le nom, aucun chiffre. L'écran fête le vainqueur en grand (« Vainqueur · Miss », photo, numéro, nom, guirlande) ; le second titre proclamé s'ajoute à côté, sur la même page.",
          "« Publier les scores » → « Oui, publier les scores » : /resultats montre rang, cumul, public et jury séparés, tour par tour, et les départages. L'écran passe à une seule page : le palmarès en haut, puis chaque concours côte à côte avec tous ses tours. Les notes de chaque juré ne sont jamais publiques.",
          "« Envoyer leurs résultats aux candidats » : chacun reçoit dans sa langue un lien privé /mes-resultats. On peut renvoyer.",
          "Les prix annexes : conducteur → « Concours » → « Prix et podiums » → « Ouvrir le podium » (ou Miss & Master → Récompenses) → « Révéler le podium » → « Révéler maintenant ».",
        ],
        warning:
          "Proclamé, plus aucun vote du concours ne se rouvre et aucun bulletin ne s'annule. On s'est trompé : voir la procédure suivante, « Je me suis trompé en publiant ».",
      },
      {
        id: 'annuler-publication',
        title: 'Je me suis trompé en publiant',
        who: 'Le même droit que le geste à défaire : Maître de cérémonie, Responsable événement, Administrateur',
        where: 'Conducteur → onglets « Concours » et « Votes » ; ou Votes → Miss & Master — tableau privé, Votes → le vote, Miss & Master → Récompenses',
        steps: [
          "Toujours dans l'ordre inverse : d'abord les scores, ensuite la proclamation. Tant que les scores sont publics, l'écran le rappelle : « Pour annuler la proclamation, annulez d'abord la publication des scores. »",
          "Scores du concours : tableau privé → « Annuler la publication des scores » → « Motif (obligatoire) » → « Retirer les scores ». Le cumul et chaque tour quittent /resultats, la fiche /mes-resultats de chaque candidat redevient « pas encore publiée », les votes reviennent à « gelé ». Le vainqueur reste proclamé.",
          "Vainqueur : « Annuler la proclamation » → motif → « Confirmer l'annulation ». Le nom quitte le site et l'écran, le candidat retrouve son statut d'avant (sauf autre titre). Le concours redevient « à proclamer » : ses votes peuvent se rouvrir, un bulletin frauduleux s'annuler, puis on proclame de nouveau.",
          "Un vote isolé publié : Votes → le vote → « Annuler la publication » (ou conducteur → onglet « Votes ») → motif → « Retirer la publication ». Les bulletins restent, le vote revient à « gelé ».",
          "Un podium révélé : conducteur → « Concours » → « Prix et podiums » → « Ouvrir le podium » (ou Miss & Master → Récompenses) → « Masquer à nouveau » → motif → « Masquer le podium ».",
        ],
        warning:
          "Le motif fait 5 caractères au moins ; il est conservé au journal d'audit et transmis à l'équipe. L'annulation n'envoie aucun e-mail — et un e-mail de résultats déjà envoyé ne se rappelle pas : son lien affiche « pas encore publié » jusqu'à la prochaine publication.",
        why: "Les pages publiques et l'écran de la salle se mettent à jour d'eux-mêmes, en une minute au plus.",
      },
      {
        id: 'annuler-bulletin',
        title: 'Annuler un bulletin frauduleux',
        who: 'Administrateur, Responsable événement',
        where: 'Votes → le vote → Dépouillement (équipe uniquement)',
        steps: [
          "« Voir les bulletins » → « Annuler ce bulletin ».",
          "Motif (5 caractères minimum) → « Confirmer l'annulation ».",
        ],
        warning: "Impossible après la proclamation. Le maître de cérémonie ne peut pas annuler un bulletin.",
      },
    ],
  },
  {
    id: 'aide-memoire',
    icon: '📇',
    phase: 'jour-j',
    title: 'Aide-mémoire par poste',
    intro: "Une fiche par poste, à lire debout. Le détail est dans les chapitres précédents.",
    procedures: [
      {
        id: 'memo-porte',
        title: 'Porte',
        who: 'Équipe (porte)',
        where: '/ops/checkin (+ /ops/pos pour la caisse de l\'entrée)',
        steps: [
          "Ouvrir la caisse de l'entrée si c'est vous qui l'avez.",
          "« Scanner » → vert : faire entrer, nom par nom.",
          "« Non réglé » (ou « Montant à régler — X € » pour une table réglée à l'entrée) : « Encaisser ici » sur le résultat du scan (Espèces → « Espèces reçues », rendre « À rendre » ; Carte → « Carte acceptée » ; PayPal vérifié → « PayPal reçu »), puis faire entrer.",
          "Table : « À placer » → la table → « Table attribuée ».",
          "Bracelet → « Bracelet remis ».",
          "Sans billet : « Entrées payées sur place » → formule → nombre → Liquide / PayPal → « Enregistrer l'entrée ».",
          "QR illisible : taper le code imprimé sous le QR ou le n° de commande → « Vérifier ».",
          "Fin : remises des serveurs (/ops/pos → Mon tiroir), puis « Clôturer » la caisse de l'entrée.",
        ],
      },
      {
        id: 'memo-serveur',
        title: 'Serveur',
        who: 'Service en salle',
        where: '/ops/service',
        steps: [
          "« Prendre » → table (ou « Sans table » + prénom) → articles → Espèces / PayPal → « Envoyer la commande ».",
          "« À encaisser » → « Encaisser » → espèces : « Montant reçu », rendre « À rendre » ; PayPal : vérifier → « PayPal vérifié ».",
          "Rien ne part avant d'être réglé.",
          "« En cours » : servir quand le poste dit « Terminé ».",
          "Remettre « En poche » à un caissier de poste. À la fin : zéro.",
        ],
      },
      {
        id: 'memo-hotesse',
        title: 'Hôtesse',
        who: "Hôtesse d'accueil",
        where: '/ops/service → Placement',
        steps: [
          "En tête, encadré : les groupes arrivés, numéro de table en grand.",
          "Accompagner → « Installer » → « Oui, installés ». La table se bloque.",
          "Mauvaise table : « Changer de table » (motif si déjà assis).",
          "Erreur : « Annuler l'installation » + motif.",
          "« Historique » : qui a fait quoi.",
        ],
      },
      {
        id: 'memo-cuisine-bar',
        title: 'Cuisine et bar',
        who: 'Cuisine, Bar',
        where: '/ops/cuisine · /ops/bar (+ /ops/pos)',
        steps: [
          "Ouvrir la caisse du poste si c'est vous qui l'avez.",
          "« En cours » : la plus ancienne d'abord ; orange 10 min, rouge 20 min.",
          "Prêt et sorti → « Terminé ».",
          "Un client paie au poste → « Encaisser » (caisse du poste ouverte).",
          "Épuisé → onglet « Carte » → « Marquer épuisé » ; « Remettre en vente » quand il revient.",
          "Impossible à servir → « Annuler » + motif.",
          "Comptoir, remises des serveurs, fermeture : « Ma caisse » → /ops/pos.",
        ],
      },
      {
        id: 'memo-caisse',
        title: 'Caisse',
        who: 'Celui qui tient une caisse',
        where: '/ops/pos',
        steps: [
          "« Poste » → « Fond de caisse » → « Prendre le poste ».",
          "Produits → moyen → « Reçu » → « Encaisser » → rendre « À rendre ».",
          "Erreur : « Mes ventes » → « Annuler cette vente » → motif → « Annuler pour de bon ».",
          "Badge « N personnes ont des espèces à remettre » → « Mon tiroir » → « Remises des serveurs » → compter → « Reçu ».",
          "Fin : dernières remises → « Clôturer » → compter → motif si écart → « Clôturer la caisse ».",
        ],
      },
      {
        id: 'memo-mc',
        title: 'Maître de cérémonie',
        who: 'Maître de cérémonie',
        where: '/ops/deroule',
        steps: [
          "Avant : onglet « Programme » — relire heures, libellés, notes ; corriger « Chevauche » / « Trou » ; vérifier le Cake Moment. Onglet « Jury » : tous les jurés sont là.",
          "« Direct » : « Lancer : … » chaque étape à l'heure.",
          "Tour : lancer l'étape → annoncer → « Ouvrir le vote » sur chaque vote du tour (sous « Sur scène »). QR du vote : onglet « Votes » → « Afficher le QR ».",
          "« Jury » → « Qui a noté quoi » tout vert ? → « Geler ».",
          "Retard : « Direct » → « Décaler la suite de N min » (ou « Programme » → « Décaler la suite ») → minutes → confirmer.",
          "Après le dernier tour : « Concours » → « Classement privé » → départage si besoin → « Proclamer le vainqueur » → « Publier les scores » → « Envoyer leurs résultats aux candidats ».",
          "Prix : « Concours » → « Prix et podiums » → « Ouvrir le podium » → « Révéler le podium ».",
          "Erreur : « Annuler la publication des scores » avant « Annuler la proclamation » ; « Masquer à nouveau » pour un podium ; motif obligatoire.",
        ],
      },
      {
        id: 'memo-regie',
        title: 'Direction (régie)',
        who: 'Administrateur (Ludovic), Responsable événement',
        where: '/ops/live',
        steps: [
          "Ouvrir la régie avant les portes, la laisser ouverte sur le téléphone.",
          "Nombre d'alertes en tête ; s'il y en a : cadre « Alertes » → « Régler » → faire le geste → revenir.",
          "« Connexion perdue… » en rouge : chiffres anciens ; « Actualiser » quand le réseau revient.",
          "Fin de nuit : « Espèces à remettre » → « Tout a été remis. » ; chaque caisse « Comptée » ; plus rien « À encaisser ».",
        ],
      },
      {
        id: 'memo-vote',
        title: 'Responsable du vote (facultatif)',
        who: 'Maître de cérémonie ou Responsable événement',
        where: 'Votes → Miss & Master — tableau privé',
        steps: [
          "Pendant chaque tour : « notes du jury : N / N » ; relancer avant le gel.",
          "Après le dernier tour : « Cumul » ; égalité → points du jury ; encore égalité → départage à 5 voix → « Geler ». QR perdus : « Réafficher les QR du départage ». Encore égalité : « Relancer un départage ».",
          "« Proclamer le vainqueur », puis « Publier les scores », puis « Envoyer leurs résultats aux candidats ».",
          "Erreur : « Annuler la publication des scores » avant « Annuler la proclamation », motif obligatoire.",
        ],
      },
      {
        id: 'memo-jure',
        title: 'Juré',
        who: 'Chaque juré',
        where: 'Son lien /jury',
        steps: [
          "Ouvrir son lien (e-mail ou QR).",
          "« Notation ouverte » : une note de 1 à 10 par candidat.",
          "La note se change tant que le tour est ouvert ; « Figé » ensuite.",
          "Lien perdu : prévenir l'équipe.",
        ],
      },
    ],
  },

  // ── Après ────────────────────────────────────────────────────────
  {
    id: 'cloture',
    icon: '📊',
    phase: 'apres',
    title: 'Compter et clôturer',
    intro: "Dans l'ordre : les serveurs remettent, les caisses ferment, la réserve compte, puis on lit et on clôture.",
    procedures: [
      {
        id: 'fin-de-nuit',
        title: 'La fin de nuit',
        who: "Toute l'équipe, le responsable de soirée",
        where: '/ops/service · /ops/pos · /ops/stock',
        steps: [
          "Fermer « Commande à table active ».",
          "Régler ou annuler toutes les commandes « À encaisser ».",
          "Chaque serveur remet tout son « En poche » à une caisse.",
          "Chaque caisse : dernières remises, puis « Clôturer ».",
          "Comptage de fermeture de la réserve.",
          "Responsable de soirée : un dernier regard sur la régie /ops/live — « Espèces à remettre » dit « Tout a été remis. », chaque caisse est « Comptée ».",
        ],
      },
      {
        id: 'comptage-fermeture',
        title: 'Comptage de stock',
        who: 'Responsable événement ou Administrateur (avec le responsable de domaine qui compte)',
        where: 'Stocks → la ligne → Comptage physique',
        steps: [
          "Compter ce qui reste réellement.",
          "Taper ce qu'on a compté, pas ce qui devrait rester → « Valider le comptage ».",
        ],
        why: "Le système calcule l'écart et l'inscrit au journal. Un comptage conforme ne produit aucune ligne.",
      },
      {
        id: 'bilan-service',
        title: 'Le bilan du service',
        who: 'Responsable finances (lecture du bilan seulement), Responsable événement, Administrateur',
        where: 'Service en salle → Le bilan du service',
        steps: [
          "Les totaux : « Net sur place » = « Net encaissé en caisse » + « Net encaissé hors caisse » ; « Espèces encore en poche » doit être à 0.",
          "« Par serveur » : en poche, commandes, espèces reçues, PayPal vérifié, rendu de sa poche, remis à une caisse.",
          "« Par poste » : fond, ventes, annulations, apports, sorties, remises, attendu, compté, écart, et l'encaissé par moyen.",
        ],
        warning: "Un serveur a encore de l'« En poche » : ses espèces manquent dans un tiroir.",
      },
      {
        id: 'cloturer-edition',
        title: "Clôturer l'édition",
        who: 'Administrateur, Responsable événement (le Responsable finances lit)',
        where: 'Clôture',
        steps: [
          "Lire le rapport : résultat, fréquentation, recettes, dépenses, caisses, stock restant.",
          "« Télécharger le rapport (CSV) ».",
          "« Clôturer l'édition ». La clôture est refusée tant qu'il y a quelque chose dans « À régler avant de clôturer » : dépenses encore soumises, commandes en attente de règlement, une caisse encore ouverte (quelle que soit son heure), des espèces encore « en poche » chez un serveur — régler, puis recommencer.",
          "« Clôturer quand même » reste possible : inscrit au journal, au nom de qui le fait, avec ce qui a été ignoré.",
        ],
        warning: "Seule une édition publiée se clôture. Close, elle disparaît du site public.",
      },
      {
        id: 'exports',
        title: 'Télécharger les exports',
        who: 'Administrateur, Responsable événement',
        where: '/ops/bilan → Exports',
        steps: [
          "Commandes, Lignes de commande, Participants, Feuille de production, Candidatures, Dépenses, Mouvements de stock, Rapport de clôture → « Télécharger ».",
          "Les fichiers CSV s'ouvrent dans Excel. Le badge « Données personnelles » prévient ; chaque téléchargement est tracé.",
        ],
      },
      {
        id: 'remercier-clients',
        title: 'Remercier les clients (liste des réservations)',
        who: "Lire : quiconque lit les commandes. Exporter : Administrateur, Responsable événement (droit « Exporter les commandes »)",
        where: 'Réservations (/admin/reservations)',
        steps: [
          "La liste montre une réservation par ligne : client, e-mail, WhatsApp s'il l'a donné, nature (billets solo/couple, table avec sa formule et son nombre de personnes, plats…), personnes, total, règlement.",
          "Par défaut : les réservations « Confirmées » (payées, avec acompte, ou à régler à l'entrée). Le filtre propose aussi « En attente de paiement », « Annulées, expirées, remboursées » et « Toutes ». Recherche par nom, e-mail, WhatsApp ou numéro de commande ; tri par date, nom, personnes ou montant.",
          "« Exporter (CSV) » télécharge exactement la sélection affichée : un fichier Excel (UTF-8, séparateur « ; »), une ligne par réservation, avec Prénom, Nom, E-mail, WhatsApp, Langue et « Accepte d'être recontacté » dans des colonnes séparées, prêtes pour un envoi groupé. Chaque export est inscrit au journal.",
          "Pour un simple merci : garder « Tous les clients ». Pour une offre, une promotion ou l'annonce de l'édition suivante : choisir « A accepté d'être recontacté » avant d'exporter.",
          "Cliquer le nom (ou « Voir le détail » sur téléphone) ouvre la réservation : contact, chaque participant, entrée comprise ou non, lignes et règlement, et pour une table son type, sa formule, son nom, les bouteilles comprises et son placement (table attribuée ou « Pas encore placée », arrivée, installée par qui).",
          "Chaque table montre sa composition initiale : les bouteilles de sa formule ou comprises dans son type (une table sur mesure : celles de son type), plus celles choisies au tunnel.",
          "« Modifier la composition » (droit « Modifier une réservation ») : personnes, nom, quantités, « − » pour retirer, et « Ajouter une boisson » — toute la carte active, par catégorie, avec recherche : une boisson absente de la formule s'ajoute d'un clic. Motif obligatoire. La formule et le prix ne changent pas ; le bar et l'hôtesse lisent aussitôt la nouvelle composition, le stock suit si la table était réglée, et le journal garde l'avant et l'après.",
          "« Changer le type de table » (même droit, motif) : la composition suit le nouveau type si elle n'a jamais été ajustée, sinon elle est gardée. Le prix ne change pas.",
        ],
        warning:
          "Un client effacé au titre du RGPD apparaît « Client effacé (RGPD) », sans e-mail ni WhatsApp : il ne doit plus rien recevoir. Le fichier contient des données personnelles — ne pas le laisser circuler, le supprimer une fois l'envoi fait.",
      },
      {
        id: 'depenses',
        title: 'Enregistrer une dépense',
        who: 'Responsable finances, Responsable événement, Administrateur',
        where: 'Finances → Dépenses',
        needs: 'La facture, en photo ou en PDF',
        steps: [
          "« Libellé », « Montant », catégorie, date → « Enregistrer » : la dépense est en Brouillon.",
          "« Ouvrir » : « Fournisseur », description, « Joindre une facture » (joindre un fichier : Administrateur ou Responsable événement).",
          "« Soumettre » : elle consomme le budget dès ce moment.",
          "Une autre personne « Approuver » (ou « Refuser » avec motif), puis « Payer ».",
          "Supprimer : seulement un Brouillon (« Supprimer ce brouillon ») ou une dépense Soumise (« Supprimer quand même »). Les autres s'annulent : « Annuler » + motif obligatoire — le montant cesse de compter, la ligne reste.",
        ],
        warning: "Nul n'approuve sa propre dépense : l'application le refuse (« Vous avez saisi cette dépense : un autre responsable doit la valider. »). Les références D-0001… ne sont jamais réutilisées, même après une suppression.",
      },
      {
        id: 'audit',
        title: 'Retrouver qui a fait quoi',
        who: 'Administrateur, Responsable événement, Responsable finances',
        where: 'Journal',
        steps: [
          "« Filtrer par action » : « finance », « inventory », « pos.sale »…",
          "Ouvrir une ligne pour voir l'avant et l'après.",
        ],
      },
    ],
  },
  {
    id: 'avis-invites',
    icon: '💬',
    phase: 'apres',
    title: 'Les avis des invités',
    intro:
      "Les invités donnent leur avis sur /avis, anonymement : une note de 0 à 10, des étoiles par catégorie, ce qu'ils ont aimé ou pas, ce qui pourrait être amélioré, s'ils recommanderaient le prochain événement, leurs suggestions. Le back-office en tire une analyse, sans IA.",
    notes: [
      {
        title: 'Anonyme, vraiment',
        body:
          "Une réponse ne garde ni nom, ni e-mail, ni adresse IP, ni navigateur, ni billet. Le lien et le QR sont les mêmes pour tous. Contre les abus : un compteur par réseau qui s'efface au bout de dix minutes, un champ piège invisible pour les robots, un temps minimum de remplissage, et une marque « déjà répondu » qui reste sur le téléphone de l'invité (une autre personne peut quand même répondre sur le même appareil).",
      },
    ],
    procedures: [
      {
        id: 'avis-partager',
        title: 'Faire répondre les invités',
        who: 'Administrateur, Responsable événement',
        where: 'Liens & QR (« Votre avis ») · Avis des invités → Inviter les invités',
        steps: [
          "Le soir même : imprimer le QR « Votre avis » (rubrique « Dans la salle ») et l'afficher en fin de soirée, ou le projeter.",
          "Le lendemain : Avis des invités → « Inviter les invités » → « Envoyer le lien d'avis aux invités » → confirmer. Les acheteurs réglés et les participants passés à l'entrée le reçoivent, une seule fois chacun.",
          "Le texte de l'e-mail se réécrit dans Messages (modèle « feedback.invitation »).",
        ],
        warning:
          "En Allemagne, une demande d'avis par e-mail peut être vue comme de la publicité : sans accord, cochez « Seulement ceux qui ont accepté d'être recontactés », ou gardez un message sans aucune offre.",
      },
      {
        id: 'avis-regler',
        title: 'Ouvrir, fermer, réécrire le formulaire',
        who: 'Administrateur, Responsable événement',
        where: 'Avis des invités → Réglages & textes',
        steps: [
          "« Recueil des avis ouvert » : fermé, la page affiche le texte « recueil fermé » et refuse les réponses (même interrupteur dans Réglages → Pages du site).",
          "Chaque texte (titre, questions, catégories, choix, remerciement, recueil fermé) se réécrit en français, allemand et anglais → « Enregistrer ». « Revenir au texte d'origine » remet le défaut.",
          "Qui reçoit un e-mail à chaque avis : Notifications e-mail → « Avis des invités ».",
        ],
      },
      {
        id: 'avis-lire',
        title: "Lire l'analyse et les avis",
        who: 'Administrateur, Responsable événement, Responsable projet (lecture)',
        where: 'Avis des invités → Analyse · Avis',
        steps: [
          "« Analyse » : résumé, bons points, points à améliorer, note moyenne, indice de recommandation, catégories classées, jour par jour, mots et expressions qui reviennent avec des citations.",
          "« Avis » : chaque réponse en entier ; la pastille du menu compte les non lus → « Marquer lu ». « Supprimer » (spam, doublon) demande un motif, gardé au journal.",
          "Les filtres (dates, notes, recommandation) valent pour l'analyse, la liste et « Exporter (CSV) ».",
        ],
        why:
          "Indice de recommandation = part des « Oui, sans hésiter » moins part des « Pas très certain » et « Pas du tout », de −100 à +100. Point fort : 4/5 et plus ; à améliorer : moins de 3,5/5.",
      },
    ],
  },
  {
    id: 'archiver',
    icon: '🧹',
    phase: 'apres',
    title: 'RGPD, effacer, recommencer',
    intro:
      "Garder des données personnelles sans raison n'est pas permis. Mais « tout supprimer » n'est jamais la réponse : on choisit par domaine.",
    procedures: [
      {
        id: 'rgpd',
        title: 'Traiter une demande RGPD',
        who: 'Administrateur (admin.gdpr.execute ; admin.gdpr.read pour lire seulement)',
        where: 'Demandes RGPD (/admin/rgpd)',
        steps: [
          "La personne fait sa demande sur /mes-donnees (« Voir », « Emporter », « Corriger » ou « Effacer mes données ») et la confirme par le lien reçu par e-mail. Avant ce clic, la demande reste « Non confirmée » et ne peut pas être exécutée.",
          "L'alerte « Demandes RGPD » prévient ; chaque demande ouverte affiche son échéance (délai légal d'un mois). Filtres « Statut » et « Type ».",
          "Une demande « À traiter » → « Exécuter » ou « Rejeter » (« Motif du refus », 5 caractères au moins).",
          "Accès et portabilité : le demandeur a déjà téléchargé son dossier, exécuter la marque traitée. Rectification : corriger d'abord dans les écrans concernés. Effacement : recopier l'adresse e-mail → « Effacer définitivement ».",
        ],
        why: "L'effacement anonymise : nom, e-mail, téléphone, WhatsApp et noms des accompagnants disparaissent ; montants, billets et heures d'entrée restent, la comptabilité doit rester juste. Le profil public d'un candidat (âge, activité, études, adage, trois mots) figure dans le dossier téléchargé et s'efface avec sa candidature.",
      },
      {
        id: 'purge',
        title: 'Supprimer des données par domaine',
        who: 'Administrateur, Responsable événement',
        where: 'Réglages → Supprimer des données',
        needs: "La clôture faite, les exports téléchargés, une sauvegarde récente",
        steps: [
          "Choisir les domaines : médias, notifications, commandes, concours, finances, stocks… Rien n'est coché d'avance.",
          "Chaque ligne annonce le nombre exact d'éléments.",
          "Retaper le nom de l'édition → « Supprimer définitivement ».",
        ],
        warning:
          "Irréversible : seule une sauvegarde peut restaurer. La purge ne touche jamais l'édition, les réglages, les modèles de message ni le journal.",
      },
      {
        id: 'dupliquer',
        title: "Préparer l'année suivante",
        who: 'Administrateur, Responsable événement',
        where: 'Édition → Éditions → « Dupliquer pour l\'an prochain »',
        steps: [
          "Nouvel « Identifiant (slug) », « Année », début ; cocher ce qu'on reprend (stocks décochés par défaut) → « Dupliquer l'édition ». Lire le rapport.",
          "Vérifier les liens PayPal, réactiver les codes promo gardés, ressaisir les stocks ; ouvrir « Ventes ouvertes » quand tout est prêt.",
          "Donner à l'équipe ses rôles sur la nouvelle édition.",
          "Archiver l'ancienne (Statut de l'édition → « Archiver »).",
        ],
      },
    ],
  },
]
